import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { jsPDF } from 'jspdf';
import axios from 'axios';
import './Invoice.css';
import Notification from '../components/Notification';
import { useT, useApp } from '../context/AppContext';
import SignaturePad from '../components/SignaturePad';

/* ══════════════════════════════════════════
   COULEURS PRÉDÉFINIES
══════════════════════════════════════════ */
const PALETTE = [
  { name: 'Violet', hex: '#667eea' },
  { name: 'Vert', hex: '#10b981' },
  { name: 'Bleu', hex: '#3b82f6' },
  { name: 'Orange', hex: '#f97316' },
  { name: 'Rouge', hex: '#ef4444' },
  { name: 'Ardoise', hex: '#475569' },
];

const hexToRgb = hex => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

/* ══════════════════════════════════════════
   SÉLECTEUR COULEUR + LOGO (réutilisable)
══════════════════════════════════════════ */
const ColorLogoSection = ({ color, onColorChange, logo, onLogoChange, onLogoRemove }) => {
  const fileRef = useRef();
  return (
    <div className="form-section">
      <h3><i className="fas fa-paint-brush" style={{ color }}></i> Couleur & Logo</h3>
      <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Palette */}
        <div>
          <label className="form-label" style={{ marginBottom: '0.6rem', display: 'block' }}>Couleur du document</label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {PALETTE.map(p => (
              <button
                key={p.hex}
                onClick={() => onColorChange(p.hex)}
                title={p.name}
                style={{
                  width: 30, height: 30, borderRadius: '50%', background: p.hex, border: 'none',
                  cursor: 'pointer', outline: color === p.hex ? `3px solid ${p.hex}` : 'none',
                  outlineOffset: 2, transition: 'transform 0.15s',
                  transform: color === p.hex ? 'scale(1.2)' : 'scale(1)',
                }}
              />
            ))}
            <input
              type="color"
              value={color}
              onChange={e => onColorChange(e.target.value)}
              title="Couleur personnalisée"
              style={{ width: 30, height: 30, borderRadius: '50%', border: '2px solid var(--border)', cursor: 'pointer', padding: 2, background: 'none' }}
            />
          </div>
        </div>

        {/* Logo */}
        <div>
          <label className="form-label" style={{ marginBottom: '0.6rem', display: 'block' }}>Logo de l'entreprise</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {logo ? (
              <>
                <img src={logo} alt="logo" style={{ height: 48, maxWidth: 120, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)', padding: 4, background: '#fff' }} />
                <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current.click()}>
                  <i className="fas fa-sync-alt"></i> Changer
                </button>
                <button className="btn btn-sm" style={{ background: '#fee2e215', color: '#ef4444', border: '1px solid #ef444430' }} onClick={onLogoRemove}>
                  <i className="fas fa-trash"></i>
                </button>
              </>
            ) : (
              <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current.click()}>
                <i className="fas fa-image"></i> Ajouter un logo
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => onLogoChange(ev.target.result);
                reader.readAsDataURL(file);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   APERÇU FACTURE
══════════════════════════════════════════ */
const InvoicePreview = ({ formData, subtotal, tax, total, onClose, onDownload, color, sigFacture, setSigFacture, paymentMethod, showSig }) => {
  const tr = useT();
  const { deviseInfo } = useApp();
  const sym = deviseInfo.symbol;
  const [r, g, b] = hexToRgb(color);
  const [showSigPad, setShowSigPad] = useState(false);

  return (
    <>
      <div className="cvp-overlay" onClick={onClose}>
        <div className="cvp-modal" onClick={e => e.stopPropagation()}>
          <div className="cvp-modal-header" style={{ borderBottom: `3px solid ${color}` }}>
            <h2><i className="fas fa-eye" style={{ color }}></i> {tr('apercu')}</h2>
            <div className="cvp-modal-actions">
              <button className="btn btn-primary btn-sm" style={{ background: `linear-gradient(135deg,${color},${color}cc)` }} onClick={onDownload}>
                <i className="fas fa-download"></i> {tr('telecharger_pdf')}
              </button>
              <button className="cvp-close" onClick={onClose}><i className="fas fa-times"></i></button>
            </div>
          </div>

          <div className="cvp-body">
            <div className="inv-sheet">
              <div className="inv-sheet-band" style={{ background: `linear-gradient(135deg, rgb(${r},${g},${b}), rgba(${r},${g},${b},0.8))` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {formData.companyLogo && (
                    <img src={formData.companyLogo} alt="logo" style={{ height: 44, maxWidth: 100, objectFit: 'contain', background: 'white', borderRadius: 6, padding: 4 }} />
                  )}
                  <div>
                    <h1 style={{ margin: 0 }}>{tr('facture').toUpperCase()}</h1>
                    {formData.companyName && <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>{formData.companyName}</div>}
                  </div>
                </div>
                <div className="inv-sheet-meta">
                  {formData.invoiceNumber && <span>N° <strong>{formData.invoiceNumber}</strong></span>}
                  <span>Date : <strong>{formData.invoiceDate}</strong></span>
                  {formData.dueDate && <span>{tr('date_echeance')} : <strong>{formData.dueDate}</strong></span>}
                </div>
              </div>

              <div className="inv-sheet-body">
                <div className="inv-parties">
                  <div className="inv-party">
                    <div className="inv-party-label" style={{ color }}>De</div>
                    <p className="inv-party-name">{formData.companyName || '—'}</p>
                    {formData.companyAddress && <p>{formData.companyAddress}</p>}
                    {formData.companyEmail && <p>{formData.companyEmail}</p>}
                    {formData.companyPhone && <p>{formData.companyPhone}</p>}
                  </div>
                  <div className="inv-party">
                    <div className="inv-party-label" style={{ color }}>À</div>
                    <p className="inv-party-name">{formData.clientName || '—'}</p>
                    {formData.clientAddress && <p>{formData.clientAddress}</p>}
                    {formData.clientEmail && <p>{formData.clientEmail}</p>}
                    {formData.clientPhone && <p>{formData.clientPhone}</p>}
                  </div>
                </div>

                <table className="inv-table">
                  <thead><tr style={{ background: `rgba(${r},${g},${b},0.08)` }}>
                    <th>{tr('description')}</th><th>{tr('quantite')}</th>
                    <th>{tr('prix_unit')}</th><th>{tr('total')}</th>
                  </tr></thead>
                  <tbody>
                    {formData.items.map((item, i) => (
                      <tr key={i}>
                        <td>{item.description || <em>—</em>}</td>
                        <td>{item.quantity}</td>
                        <td>{parseFloat(item.price).toFixed(2)} {sym}</td>
                        <td>{(parseFloat(item.quantity) * parseFloat(item.price)).toFixed(2)} {sym}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="inv-totals">
                  <div className="inv-total-row"><span>{tr('sous_total')}</span><span>{subtotal.toFixed(2)} {sym}</span></div>
                  <div className="inv-total-row"><span>TVA ({formData.taxRate}%)</span><span>{tax.toFixed(2)} {sym}</span></div>
                  <div className="inv-total-row inv-total-final" style={{ borderTopColor: color, color }}>
                    <span>{tr('total_ttc')}</span><span>{total.toFixed(2)} {sym}</span>
                  </div>
                </div>

                {formData.notes && (
                  <div className="inv-notes" style={{ borderLeftColor: color }}>
                    <strong>{tr('notes')} :</strong> {formData.notes}
                  </div>
                )}

                {paymentMethod && (
                  <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', background: `${color}10`, borderRadius: '8px', border: `1px solid ${color}30` }}>
                    <i className="fas fa-wallet" style={{ color }}></i>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      Mode de paiement : <span style={{ color }}>{paymentMethod}</span>
                    </span>
                  </div>
                )}

                {/* Signatures seulement si showSig = true */}
                {showSig && (
                  <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                    {/* ... ton code de signatures existant ... */}
                  </div>
                )}

                {/* ══ SIGNATURES ══ */}
                <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>

                  {/* Prestataire */}
                  <div style={{ border: `1px dashed ${color}60`, borderRadius: '12px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <i className="fas fa-pen"></i> {formData.companyName || 'Prestataire'}
                    </div>

                    {sigFacture ? (
                      <div style={{ position: 'relative' }}>
                        <img src={sigFacture} alt="signature" style={{ width: '100%', height: 55, objectFit: 'contain', borderBottom: `1px solid ${color}40` }} />
                        <button onClick={() => setSigFacture(null)} style={{ position: 'absolute', top: 0, right: 0, background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '2px 6px', cursor: 'pointer', color: '#ef4444', fontSize: '0.7rem' }}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowSigPad(true)}
                        style={{
                          height: 55, background: `${color}06`, border: `1.5px dashed ${color}40`,
                          borderRadius: '8px', cursor: 'pointer', color, fontSize: '0.82rem',
                          fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = `${color}12`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = `${color}06`; }}
                      >
                        <i className="fas fa-signature"></i> Cliquer pour signer
                      </button>
                    )}
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                      {sigFacture ? `Signé le ${new Date().toLocaleDateString('fr-FR')}` : 'Signature'}
                    </div>
                  </div>

                  {/* Client — ligne + date + nom */}
                  <div style={{ border: `2px solid ${color}`, borderRadius: '12px', padding: '1rem', background: `${color}06`, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <i className="fas fa-check-circle"></i> {formData.clientName || 'Client'}
                    </div>
                    <div style={{ height: 55, borderBottom: `1px solid ${color}40` }}></div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                      Date : {new Date().toLocaleDateString('fr-FR')}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSigPad && (
        <SignaturePad
          title={`Signature — ${formData.companyName || 'Prestataire'}`}
          onSave={dataUrl => { setSigFacture(dataUrl); setShowSigPad(false); }}
          onCancel={() => setShowSigPad(false)}
        />
      )}
    </>
  );
};

/* ══════════════════════════════════════════
   APERÇU DEVIS
══════════════════════════════════════════ */
const DevisPreview = ({ devisData, subtotal, tax, total, onClose, onDownload, onConvertToInvoice, color, sigPrestataire, setSigPrestataire, devisOptions, paymentMethod, showSig }) => {
  const tr = useT();
  const { deviseInfo } = useApp();
  const sym = deviseInfo.symbol;
  const [r, g, b] = hexToRgb(color);

  // — Signatures —
  /*const [sigPrestataire, setSigPrestataire] = useState(null);
  const [showSigPad, setShowSigPad] = useState(null); */// 'prestataire' | 'client'
  const [showSigPad, setShowSigPad] = useState(null);

  const statutColors = {
    'Brouillon': { bg: '#6b728020', color: '#6b7280' },
    'Envoyé': { bg: '#3b82f620', color: '#3b82f6' },
    'Accepté': { bg: '#10b98120', color: '#10b981' },
    'Refusé': { bg: '#f43f5e20', color: '#f43f5e' },
  };
  const sc = statutColors[devisData.statut] || statutColors['Brouillon'];

  return (
    <>
      <div className="cvp-overlay" onClick={onClose}>
        <div className="cvp-modal" onClick={e => e.stopPropagation()}>

          {/* Header modal */}
          <div className="cvp-modal-header" style={{ borderBottom: `3px solid ${color}` }}>
            <h2><i className="fas fa-file-contract" style={{ color }}></i> Aperçu du devis</h2>
            <div className="cvp-modal-actions">
              {devisData.statut === 'Accepté' && (
                <button className="btn btn-secondary btn-sm" onClick={onConvertToInvoice}>
                  <i className="fas fa-file-invoice"></i> Convertir en facture
                </button>
              )}
              <button className="btn btn-primary btn-sm" style={{ background: `linear-gradient(135deg,${color},${color}cc)` }} onClick={onDownload}>
                <i className="fas fa-download"></i> Télécharger PDF
              </button>
              <button className="cvp-close" onClick={onClose}><i className="fas fa-times"></i></button>
            </div>
          </div>

          <div className="cvp-body">
            <div className="inv-sheet">

              {/* Bandeau */}
              <div className="inv-sheet-band" style={{ background: `linear-gradient(135deg, rgb(${r},${g},${b}), rgba(${r},${g},${b},0.8))` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {devisData.companyLogo && (
                      <img src={devisData.companyLogo} alt="logo" style={{ height: 44, maxWidth: 100, objectFit: 'contain', background: 'white', borderRadius: 6, padding: 4 }} />
                    )}
                    <div>
                      <h1 style={{ margin: 0 }}>DEVIS</h1>
                      {devisData.objet && <div style={{ fontSize: '0.88rem', opacity: 0.9, marginTop: '0.2rem' }}>{devisData.objet}</div>}
                    </div>
                  </div>
                  <span style={{ background: sc.bg, color: sc.color, padding: '0.3rem 1rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 700, backdropFilter: 'blur(4px)' }}>
                    {devisData.statut}
                  </span>
                </div>
                <div className="inv-sheet-meta" style={{ marginTop: '0.75rem' }}>
                  {devisData.devisNumber && <span>N° <strong>{devisData.devisNumber}</strong></span>}
                  <span>Émis le : <strong>{devisData.devisDate}</strong></span>
                  {devisData.validUntil && <span style={{ background: 'rgba(255,255,255,0.2)', padding: '0.15rem 0.6rem', borderRadius: '10px' }}><i className="fas fa-clock" style={{ marginRight: '4px' }}></i> Valable jusqu'au <strong>{devisData.validUntil}</strong></span>}
                </div>
              </div>

              <div className="inv-sheet-body">
                {/* Parties */}
                <div className="inv-parties">
                  <div className="inv-party">
                    <div className="inv-party-label" style={{ color }}>De</div>
                    <p className="inv-party-name">{devisData.companyName || '—'}</p>
                    {devisData.companyAddress && <p>{devisData.companyAddress}</p>}
                    {devisData.companyEmail && <p>{devisData.companyEmail}</p>}
                  </div>
                  <div className="inv-party">
                    <div className="inv-party-label" style={{ color }}>À</div>
                    <p className="inv-party-name">{devisData.clientName || '—'}</p>
                    {devisData.clientAddress && <p>{devisData.clientAddress}</p>}
                    {devisData.clientEmail && <p>{devisData.clientEmail}</p>}
                  </div>
                </div>

                {/* Tableau */}
                <table className="inv-table">
                  <thead><tr style={{ background: `rgba(${r},${g},${b},0.08)` }}>
                    <th>{tr('description')}</th><th>{tr('quantite')}</th>
                    <th>{tr('prix_unit')}</th><th>{tr('total')}</th>
                  </tr></thead>
                  <tbody>
                    {devisData.items.map((item, i) => (
                      <tr key={i}>
                        <td>{item.description || <em>—</em>}</td>
                        <td>{item.quantity}</td>
                        <td>{parseFloat(item.price).toFixed(2)} {sym}</td>
                        <td>{(parseFloat(item.quantity) * parseFloat(item.price)).toFixed(2)} {sym}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totaux */}
                <div className="inv-totals">
                  <div className="inv-total-row"><span>{tr('sous_total')}</span><span>{subtotal.toFixed(2)} {sym}</span></div>
                  <div className="inv-total-row"><span>TVA ({devisData.taxRate}%)</span><span>{tax.toFixed(2)} {sym}</span></div>
                  {devisData.acompte > 0 && (
                    <div className="inv-total-row" style={{ color: '#f97316' }}>
                      <span>Acompte ({devisData.acompte}%)</span>
                      <span>{(total * devisData.acompte / 100).toFixed(2)} {sym}</span>
                    </div>
                  )}
                  <div className="inv-total-row inv-total-final" style={{ borderTopColor: color, color }}>
                    <span>{tr('total_ttc')}</span><span>{total.toFixed(2)} {sym}</span>
                  </div>
                </div>

                {/* Notes */}
                {devisData.notes && (
                  <div className="inv-notes" style={{ borderLeftColor: color }}>
                    <strong>Conditions :</strong> {devisData.notes}
                  </div>
                )}

                {paymentMethod && (
                  <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', background: `${color}10`, borderRadius: '8px', border: `1px solid ${color}30` }}>
                    <i className="fas fa-wallet" style={{ color }}></i>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      Mode de paiement : <span style={{ color }}>{paymentMethod}</span>
                    </span>
                  </div>
                )}

                {showSig && (
                  <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                    {/* ... ton code de signatures existant ... */}
                  </div>
                )}

                {/* Options facultatives dans l'aperçu */}
                {devisOptions && devisOptions.length > 0 && (
                  <div style={{ marginTop: '1.25rem' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', color, letterSpacing: '0.08em', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <i className="fas fa-puzzle-piece"></i> Options facultatives
                    </div>
                    <table className="inv-table" style={{ opacity: 0.85 }}>
                      <thead>
                        <tr style={{ background: `rgba(${r},${g},${b},0.06)` }}>
                          <th>Option</th>
                          <th>Description</th>
                          <th style={{ textAlign: 'right' }}>Prix</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devisOptions.map((opt, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 600 }}>
                              <span style={{ fontSize: '0.7rem', background: `${color}15`, color, padding: '0.1rem 0.5rem', borderRadius: '10px', marginRight: '0.4rem', fontWeight: 700 }}>OPT</span>
                              {opt.label || '—'}
                            </td>
                            <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{opt.description || '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color }}>{parseFloat(opt.price || 0).toFixed(2)} {sym}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.4rem', fontStyle: 'italic' }}>
                      * Ces options sont facultatives et non incluses dans le montant total du devis.
                    </div>
                  </div>
                )}

                {/* ══ ZONES DE SIGNATURE ══ */}
                <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>

                  {/* Prestataire */}
                  <div style={{ border: `1px dashed ${color}60`, borderRadius: '12px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <i className="fas fa-pen"></i> Signature prestataire
                    </div>

                    {sigPrestataire ? (
                      <div style={{ position: 'relative' }}>
                        <img src={sigPrestataire} alt="signature" style={{ width: '100%', height: 60, objectFit: 'contain', borderBottom: `1px solid ${color}40` }} />
                        <button
                          onClick={() => setSigPrestataire(null)}
                          style={{ position: 'absolute', top: 0, right: 0, background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '2px 6px', cursor: 'pointer', color: '#ef4444', fontSize: '0.7rem' }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowSigPad('prestataire')}
                        style={{
                          height: 60, background: `${color}06`, border: `1.5px dashed ${color}40`,
                          borderRadius: '8px', cursor: 'pointer', color, fontSize: '0.82rem',
                          fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = `${color}12`; e.currentTarget.style.borderStyle = 'solid'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = `${color}06`; e.currentTarget.style.borderStyle = 'dashed'; }}
                      >
                        <i className="fas fa-signature"></i> Cliquer pour signer
                      </button>
                    )}

                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{devisData.companyName}</div>
                  </div>

                  {/* Client */}
                  <div style={{ border: `2px solid ${color}`, borderRadius: '12px', padding: '1rem', background: `${color}06`, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <i className="fas fa-check-circle"></i> Bon pour accord — Client
                    </div>
                    <div style={{ height: 60, borderBottom: `1px solid ${color}40` }}></div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                      Date : {new Date().toLocaleDateString('fr-FR')}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pad de signature */}
      {showSigPad && (
        <SignaturePad
          title="Signature prestataire"
          onSave={dataUrl => {
            setSigPrestataire(dataUrl);
            setShowSigPad(null);
          }}
          onCancel={() => setShowSigPad(null)}
        />
      )}
    </>
  );
};

/* ══════════════════════════════════════════
   MODAL CLIENT
══════════════════════════════════════════ */
const ClientModal = ({ client, onClose, onSave }) => {
  const tr = useT();
  const [form, setForm] = useState(client || {
    type: 'particulier', name: '', email: '', phone: '',
    address: '', city: '', country: '', siret: '', tva: '', notes: '',
  });
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  return (
    <div className="cvp-overlay" onClick={onClose}>
      <div className="cvp-modal" onClick={e => e.stopPropagation()}>
        <div className="cvp-modal-header">
          <h2><i className={`fas ${client ? 'fa-edit' : 'fa-user-plus'}`}></i>
            {client ? ` ${tr('modifier')}` : ` ${tr('nouveau_client')}`}
          </h2>
          <button className="cvp-close" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>
        <div className="cvp-body" style={{ background: 'var(--bg-page)' }}>
          <div className="client-modal-form">
            <div className="client-type-toggle">
              <button className={`client-type-btn ${form.type === 'particulier' ? 'active' : ''}`} onClick={() => setForm({ ...form, type: 'particulier' })}>
                <i className="fas fa-user"></i> {tr('particuliers')}
              </button>
              <button className={`client-type-btn ${form.type === 'societe' ? 'active' : ''}`} onClick={() => setForm({ ...form, type: 'societe' })}>
                <i className="fas fa-building"></i> {tr('societes')}
              </button>
            </div>
            <div className="form-grid">
              <div className="form-group-settings full-width">
                <label>{form.type === 'societe' ? `${tr('nom_entreprise')} *` : `${tr('nom_client')} *`}</label>
                <input className="input-settings" name="name" value={form.name} onChange={handleChange} placeholder={form.type === 'societe' ? 'FlowKraft Agency' : 'Jean Dupont'} />
              </div>
              <div className="form-group-settings">
                <label>{tr('email')}</label>
                <input className="input-settings" name="email" type="email" value={form.email} onChange={handleChange} />
              </div>
              <div className="form-group-settings">
                <label>{tr('telephone')}</label>
                <input className="input-settings" name="phone" value={form.phone} onChange={handleChange} placeholder="+228 99 00 00 00" />
              </div>
              <div className="form-group-settings full-width">
                <label>{tr('adresse')}</label>
                <input className="input-settings" name="address" value={form.address} onChange={handleChange} />
              </div>
              <div className="form-group-settings">
                <label>Ville</label>
                <input className="input-settings" name="city" value={form.city} onChange={handleChange} placeholder="Lomé" />
              </div>
              <div className="form-group-settings">
                <label>Pays</label>
                <input className="input-settings" name="country" value={form.country} onChange={handleChange} placeholder="Togo" />
              </div>
              {form.type === 'societe' && (
                <>
                  <div className="form-group-settings">
                    <label>SIRET</label>
                    <input className="input-settings" name="siret" value={form.siret} onChange={handleChange} />
                  </div>
                  <div className="form-group-settings">
                    <label>{tr('tva')}</label>
                    <input className="input-settings" name="tva" value={form.tva} onChange={handleChange} />
                  </div>
                </>
              )}
              <div className="form-group-settings full-width">
                <label>{tr('notes')}</label>
                <textarea className="input-settings" name="notes" value={form.notes} onChange={handleChange} rows={3} style={{ resize: 'vertical' }} />
              </div>
            </div>
            <div className="modal-actions-security" style={{ marginTop: '1.5rem' }}>
              <button className="btn-modal-cancel" onClick={onClose}>{tr('annuler')}</button>
              <button className="btn-modal-confirm" onClick={() => { if (form.name.trim()) onSave(form); }} disabled={!form.name.trim()}>
                <i className="fas fa-save"></i> {client ? ` ${tr('modifier')}` : ` ${tr('nouveau_client')}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientSelector = ({ value, onChange, onSelect, clients, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value || '');
  const ref = useRef(null);

  useEffect(() => { setSearch(value || ''); }, [value]);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          type="text"
          className="form-input"
          value={search}
          placeholder={placeholder || 'Nom du client ou rechercher...'}
          onChange={e => { setSearch(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        {clients.length > 0 && (
          <i
            className={`fas fa-chevron-${open ? 'up' : 'down'}`}
            onClick={() => setOpen(v => !v)}
            style={{ position: 'absolute', right: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}
          />
        )}
      </div>

      {open && clients.length > 0 && (
        <div style={{
          position: 'absolute', top: '110%', left: 0, right: 0, zIndex: 999,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--border-radius-lg)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          maxHeight: 220, overflowY: 'auto',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '0.85rem 1rem', color: 'var(--text-secondary)', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i className="fas fa-user-plus" style={{ color: 'var(--accent-start)' }}></i>
              Nouveau client — tapez et sauvegardez
            </div>
          ) : (
            filtered.map(c => (
              <div
                key={c.id}
                onClick={() => { onSelect(c); setSearch(c.name); setOpen(false); }}
                style={{
                  padding: '0.75rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  gap: '0.75rem', transition: 'background 0.15s', borderBottom: '1px solid var(--border)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, var(--accent-start), var(--accent-end))',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.82rem', fontWeight: 700,
                }}>
                  {c.type === 'societe' ? <i className="fas fa-building" style={{ fontSize: '0.7rem' }}></i> : c.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                  {c.email && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</div>}
                </div>
                <span style={{ fontSize: '0.7rem', background: 'rgba(102,126,234,0.1)', color: 'var(--accent-start)', padding: '0.15rem 0.5rem', borderRadius: '10px', flexShrink: 0 }}>
                  {c.type === 'societe' ? 'Société' : 'Particulier'}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const PAYMENT_METHODS = [
  { value: 'Virement bancaire', label: 'Virement bancaire', icon: 'fa-university' },
  { value: 'Carte bancaire', label: 'Carte bancaire', icon: 'fa-credit-card' },
  { value: 'Chèque', label: 'Chèque', icon: 'fa-money-check' },
  { value: 'Espèces', label: 'Espèces', icon: 'fa-money-bill-wave' },
  { value: 'PayPal', label: 'PayPal', icon: 'fa-paypal' },
  { value: 'Mobile Money', label: 'Mobile Money', icon: 'fa-mobile-alt' },
  { value: 'T-Money', label: 'T-Money', icon: 'fa-mobile-alt' },  // ← nouveau
  { value: 'Flooz', label: 'Flooz', icon: 'fa-mobile-alt' },
];

const PaymentSelector = ({ values = [], onChange, color }) => (
  <div className="form-section">
    <h3><i className="fas fa-wallet" style={{ color }}></i> Mode(s) de paiement acceptés</h3>
    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
      {PAYMENT_METHODS.map(p => {
        const selected = values.includes(p.value);
        return (
          <button
            key={p.value}
            type="button"
            onClick={() => {
              if (selected) onChange(values.filter(v => v !== p.value));
              else onChange([...values, p.value]);
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.55rem 1rem', borderRadius: '10px', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 500, transition: 'all 0.2s',
              border: selected ? `2px solid ${color}` : '1px solid var(--border)',
              background: selected ? `${color}15` : 'var(--bg-card)',
              color: selected ? color : 'var(--text-secondary)',
            }}
          >
            <i className={`fas ${p.icon}`} style={{ fontSize: '0.9rem' }}></i>
            {p.value}
            {selected && <i className="fas fa-check" style={{ fontSize: '0.7rem' }}></i>}
          </button>
        );
      })}
    </div>
    {values.length > 0 && (
      <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {values.map(v => (
          <span key={v} style={{
            fontSize: '0.82rem', color, fontWeight: 600,
            background: `${color}10`, padding: '0.2rem 0.65rem',
            borderRadius: '20px', border: `1px solid ${color}30`,
            display: 'flex', alignItems: 'center', gap: '0.4rem',
          }}>
            <i className="fas fa-check-circle" style={{ fontSize: '0.7rem' }}></i> {v}
          </span>
        ))}
      </div>
    )}
  </div>
);

/* ══════════════════════════════════════════
   HELPERS NUMÉROTATION
══════════════════════════════════════════ */
const getDocSettings = () => { try { return JSON.parse(localStorage.getItem('doc_settings') || '{}'); } catch { return {}; } };
const buildInvoiceNumber = () => { const s = getDocSettings(); return `${s.invoicePrefix||'INV'}-${new Date().getFullYear()}-${String(s.invoiceNextNum||1).padStart(3,'0')}`; };
const buildDevisNumber   = () => { const s = getDocSettings(); return `${s.devisPrefix||'DEV'}-${new Date().getFullYear()}-${String(s.devisNextNum||1).padStart(3,'0')}`; };
const incrementInvoiceNum = () => { const s = getDocSettings(); localStorage.setItem('doc_settings', JSON.stringify({...s, invoiceNextNum:(s.invoiceNextNum||1)+1})); };
const incrementDevisNum   = () => { const s = getDocSettings(); localStorage.setItem('doc_settings', JSON.stringify({...s, devisNextNum:(s.devisNextNum||1)+1})); };

/* ══════════════════════════════════════════
   COMPOSANT PRINCIPAL
══════════════════════════════════════════ */
const Invoice = () => {
  const [activeTab, setActiveTab] = useState('facture');
  const [notification, setNotification] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDevisPreview, setShowDevisPreview] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [sigPrestataire, setSigPrestataire] = useState(null);
  const [sigFacture, setSigFacture] = useState(null);
  const [devisOptions, setDevisOptions] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showInvoiceSig, setShowInvoiceSig] = useState(false);
  const [showDevisSig, setShowDevisSig] = useState(false);
  const [invoicePaymentMethods, setInvoicePaymentMethods] = useState([]);
  const [devisPaymentMethods, setDevisPaymentMethods] = useState([]);

  const tr = useT();
  const { deviseInfo } = useApp();
  const sym = deviseInfo.symbol;

  // ── Couleurs ──
  const [invoiceColor, setInvoiceColor] = useState('#667eea');
  const [devisColor, setDevisColor] = useState('#10b981');

  // ── Clients ──
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('all');

  // ── Factures sauvegardées ──
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [savedInvoicesLoading, setSavedInvoicesLoading] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);

  // ── CRM ──
  const [crmView, setCrmView] = useState('kanban');
  const [crmSection, setCrmSection] = useState('opportunities');
  const [opportunities, setOpportunities] = useState([]);
  const [newOpp, setNewOpp] = useState({
    destinataire: '', intitule: '', montantHT: '', devise: deviseInfo.code,
    moisSignature: '', anneeSignature: '', statut: 'Prospect',
  });
  const [activities, setActivities] = useState([]);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityFilter, setActivityFilter] = useState('tous');
  const [showExportModal, setShowExportModal] = useState(false);
  const [newActivity, setNewActivity] = useState({
    titre: '', date: new Date().toISOString().split('T')[0],
    heure: new Date().toTimeString().slice(0, 5),
    notes: '', realise: false, responsable: '',
  });

  // ── Facture ──
  const [formData, setFormData] = useState({
    companyName: '', companyAddress: '', companyPhone: '', companyEmail: '',
    clientName: '', clientAddress: '', clientPhone: '', clientEmail: '',
    invoiceNumber: buildInvoiceNumber(),
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '', paidDate: '', paymentMethod: '',
    items: [{ description: '', quantity: 1, price: 0 }],
    notes: '', taxRate: 20,
    companyLogo: null,
  });

  // ── Devis ──
  const [devisData, setDevisData] = useState({
    companyName: '', companyAddress: '', companyEmail: '',
    clientName: '', clientAddress: '', clientEmail: '',
    devisNumber: buildDevisNumber(),
    devisDate: new Date().toISOString().split('T')[0],
    validUntil: '', objet: '', statut: 'Brouillon', acompte: 0,
    items: [{ description: '', quantity: 1, price: 0 }],
    notes: '', taxRate: 20,
    companyLogo: null,
    details: '',
  });


  /* ── Notif ── */
  const showNotif = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  };

  /* ── Load ── */
  useEffect(() => {
    if (activeTab === 'clients' || activeTab === 'crm') loadClients();
    if (activeTab === 'historique') loadSavedInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ── Clients API ── */
  const loadClients = async () => {
    setClientsLoading(true);
    try {
      const res = await axios.get('/clients');
      setClients(res.data.clients || []);
    } catch { showNotif(tr('erreur_chargement'), 'error'); }
    finally { setClientsLoading(false); }
  };

  const handleSaveClient = async (form) => {
    try {
      if (editingClient) {
        await axios.put(`/clients/${editingClient.id}`, form);
        showNotif(tr('client_modifie'));
      } else {
        await axios.post('/clients', form);
        showNotif(tr('client_cree'));
      }
      setShowClientModal(false); setEditingClient(null);
      loadClients();
    } catch { showNotif(tr('erreur_sauvegarde'), 'error'); }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm(`${tr('supprimer')} ?`)) return;
    try {
      await axios.delete(`/clients/${id}`);
      showNotif(tr('client_supprime')); loadClients();
    } catch { showNotif(tr('erreur_suppression'), 'error'); }
  };

  const handleSelectClient = (client) => {
    setFormData(prev => ({
      ...prev,
      clientName: client.name,
      clientAddress: `${client.address || ''} ${client.city || ''} ${client.country || ''}`.trim(),
      clientPhone: client.phone || '',
      clientEmail: client.email || '',
    }));
    setActiveTab('facture');
    showNotif(`"${client.name}" ${tr('choisir_client')}`);
  };

  const handleSelectClientForDevis = (client) => {
    setDevisData(prev => ({
      ...prev,
      clientName: client.name,
      clientAddress: `${client.address || ''} ${client.city || ''} ${client.country || ''}`.trim(),
      clientEmail: client.email || '',
    }));
    setActiveTab('devis');
    showNotif(`"${client.name}" ajouté au devis`);
  };

  /* ── Sauvegarder client depuis formulaire ── */
  const handleSaveClientFromForm = async (source = 'facture') => {
    const data = source === 'facture' ? formData : devisData;
    if (!data.clientName.trim()) { showNotif('Nom du client requis', 'error'); return; }
    try {
      await axios.post('/clients', {
        type: 'particulier',
        name: data.clientName,
        email: data.clientEmail || '',
        phone: data.clientPhone || '',
        address: data.clientAddress || '',
      });
      showNotif(`"${data.clientName}" sauvegardé dans les clients ✓`);
      loadClients();
    } catch { showNotif(tr('erreur_sauvegarde'), 'error'); }
  };

  /* ── Factures & Devis sauvegardés ── */
  const loadSavedInvoices = async () => {
    setSavedInvoicesLoading(true);
    try {
      const res = await axios.get('/pdf');
      setSavedInvoices((res.data.pdfs || []).filter(p => p.type === 'invoice' || p.type === 'quote'));
    } catch { showNotif(tr('erreur_chargement'), 'error'); }
    finally { setSavedInvoicesLoading(false); }
  };

  const handleEditSavedInvoice = (doc) => {
    try {
      const data = typeof doc.data === 'string' ? JSON.parse(doc.data) : doc.data;
      setFormData({ ...formData, ...data });
      setEditingInvoiceId(doc.id);
      setActiveTab('facture');
      showNotif('Facture chargée pour modification ✓');
    } catch { showNotif('Impossible de charger cette facture', 'error'); }
  };

  const handleEditSavedDevis = (doc) => {
    try {
      const data = typeof doc.data === 'string' ? JSON.parse(doc.data) : doc.data;
      setDevisData(prev => ({ ...prev, ...data }));
      setActiveTab('devis');
      showNotif('Devis chargé pour modification ✓');
    } catch { showNotif('Impossible de charger ce devis', 'error'); }
  };

  /* ── Convertir devis → facture ── */
  const handleConvertToInvoice = () => {
    setFormData(prev => ({
      ...prev,
      companyName: devisData.companyName,
      companyAddress: devisData.companyAddress,
      companyEmail: devisData.companyEmail,
      companyLogo: devisData.companyLogo,
      clientName: devisData.clientName,
      clientAddress: devisData.clientAddress,
      clientEmail: devisData.clientEmail,
      invoiceNumber: `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
      invoiceDate: new Date().toISOString().split('T')[0],
      items: devisData.items,
      notes: devisData.notes,
      taxRate: devisData.taxRate,
    }));
    setInvoiceColor(devisColor);
    setActiveTab('facture');
    setShowDevisPreview(false);
    showNotif('Devis converti en facture ✓');
  };

  /* ── CRM ── */
  const handleCreateOpportunity = () => {
    if (!newOpp.intitule.trim()) { showNotif(tr('intitule') + ' requis', 'error'); return; }
    setOpportunities(prev => [...prev, { id: Date.now(), ...newOpp, createdAt: new Date().toISOString() }]);
    setNewOpp({ destinataire: '', intitule: '', montantHT: '', devise: deviseInfo.code, moisSignature: '', anneeSignature: '', statut: 'Prospect' });
    setCrmView('kanban'); showNotif(tr('opp_creee'));
  };

  const handleCreateActivity = () => {
    if (!newActivity.titre.trim()) { showNotif(tr('intitule') + ' requis', 'error'); return; }
    setActivities(prev => [...prev, { id: Date.now(), ...newActivity, createdAt: new Date().toISOString() }]);
    setNewActivity({ titre: '', notes: '', realise: false, responsable: '', date: new Date().toISOString().split('T')[0], heure: new Date().toTimeString().slice(0, 5) });
    setShowActivityModal(false); showNotif('Activité créée !');
  };

  const handleToggleActivity = (id) => setActivities(prev => prev.map(a => a.id === id ? { ...a, realise: !a.realise } : a));
  const handleDeleteActivity = (id) => setActivities(prev => prev.filter(a => a.id !== id));

  const getActivityStatus = (act) => {
    if (act.realise) return 'terminee';
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dueDay = new Date(act.date); dueDay.setHours(0, 0, 0, 0);
    if (dueDay < today) return 'retard';
    if (dueDay.getTime() === today.getTime()) return 'aujourdhui';
    return 'avenir';
  };

  const exportActivities = (format) => {
    const data = activities;
    if (format === 'csv') {
      const rows = data.map(a => `"${a.titre}","${a.date}","${a.heure}","${a.notes}","${a.realise ? 'Oui' : 'Non'}","${a.responsable}"`).join('\n');
      const blob = new Blob(['Titre,Date,Heure,Notes,Réalisé,Responsable\n' + rows], { type: 'text/csv;charset=utf-8;' });
      const el = document.createElement('a'); el.href = URL.createObjectURL(blob); el.download = 'activites.csv'; el.click();
    } else if (format === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const el = document.createElement('a'); el.href = URL.createObjectURL(blob); el.download = 'activites.json'; el.click();
    } else if (format === 'pdf') {
      const doc = new jsPDF();
      doc.setFontSize(16); doc.setFont('helvetica', 'bold');
      doc.text('Liste des Activités', 20, 20);
      doc.setFontSize(9); doc.setFont('helvetica', 'normal');
      let y = 35;
      data.forEach((a, i) => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
        doc.text(`${i + 1}. ${a.titre}`, 20, y); y += 6;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
        doc.text(`Échéance : ${a.date} à ${a.heure}  |  Réalisé : ${a.realise ? 'Oui' : 'Non'}`, 25, y); y += 5;
        if (a.notes) { doc.text(`Notes : ${a.notes}`, 25, y); y += 5; }
        y += 3;
      });
      doc.save('activites.pdf');
    }
    setShowExportModal(false);
  };

  /* ── Facture handlers ── */
  const handleChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleItemChange = (i, f, v) => { const items = [...formData.items]; items[i][f] = v; setFormData({ ...formData, items }); };
  const addItem = () => setFormData({ ...formData, items: [...formData.items, { description: '', quantity: 1, price: 0 }] });
  const removeItem = i => { if (formData.items.length > 1) setFormData({ ...formData, items: formData.items.filter((_, idx) => idx !== i) }); };

  const calculateSubtotal = () => formData.items.reduce((s, it) => s + parseFloat(it.quantity || 0) * parseFloat(it.price || 0), 0);
  const calculateTax = () => calculateSubtotal() * (parseFloat(formData.taxRate || 0) / 100);
  const calculateTotal = () => calculateSubtotal() + calculateTax();

  /* ── Devis handlers ── */
  const handleDevisChange = e => setDevisData({ ...devisData, [e.target.name]: e.target.value });
  const handleDevisItemChange = (i, f, v) => { const items = [...devisData.items]; items[i][f] = v; setDevisData({ ...devisData, items }); };
  const addDevisItem = () => setDevisData({ ...devisData, items: [...devisData.items, { description: '', quantity: 1, price: 0 }] });
  const removeDevisItem = i => { if (devisData.items.length > 1) setDevisData({ ...devisData, items: devisData.items.filter((_, idx) => idx !== i) }); };

  const calcDevisSubtotal = () => devisData.items.reduce((s, it) => s + parseFloat(it.quantity || 0) * parseFloat(it.price || 0), 0);
  const calcDevisTax = () => calcDevisSubtotal() * (parseFloat(devisData.taxRate || 0) / 100);
  const calcDevisTotal = () => calcDevisSubtotal() + calcDevisTax();

  /* ── PDF Facture ── */
  /* ── PDF Facture — capture du rendu HTML ── */
  const generatePDF = async () => {
    try {
      // Ouvre l'aperçu si pas encore ouvert, sinon capture directement
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF: JsPDF } = await import('jspdf');

      // Crée un conteneur temporaire hors écran
      const container = document.createElement('div');
      container.style.cssText = `
      position: fixed; top: -9999px; left: -9999px;
      width: 794px; background: white; z-index: -1;
    `;
      document.body.appendChild(container);

      // Rendu React dans ce conteneur via un portal
      const ReactDOM = await import('react-dom');

      const SheetContent = () => {
        const s = sym;
        const [r2, g2, b2] = hexToRgb(invoiceColor);
        const subtotal = calculateSubtotal();
        const tax = calculateTax();
        const total = calculateTotal();

        return (
          <div style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '13px', color: '#1a1a1a', background: '#fff' }}>
            {/* Bandeau */}
            <div style={{ background: `linear-gradient(135deg, rgb(${r2},${g2},${b2}), rgba(${r2},${g2},${b2},0.85))`, color: '#fff', padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {formData.companyLogo && <img src={formData.companyLogo} alt="logo" style={{ height: 48, maxWidth: 110, objectFit: 'contain', background: '#fff', borderRadius: 6, padding: 4 }} />}
                <div>
                  <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: 2 }}>FACTURE</div>
                  {formData.companyName && <div style={{ fontSize: 12, opacity: 0.85 }}>{formData.companyName}</div>}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 11, opacity: 0.92 }}>
                {formData.invoiceNumber && <div>N° <strong>{formData.invoiceNumber}</strong></div>}
                <div>Date : <strong>{formData.invoiceDate}</strong></div>
                {formData.dueDate && <div>Échéance : <strong>{formData.dueDate}</strong></div>}
              </div>
            </div>

            {/* Parties */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, padding: '20px 28px 10px' }}>
              {[{ label: 'DE', name: formData.companyName, addr: formData.companyAddress, email: formData.companyEmail, phone: formData.companyPhone },
              { label: 'À', name: formData.clientName, addr: formData.clientAddress, email: formData.clientEmail, phone: formData.clientPhone }
              ].map((p, i) => (
                <div key={i}>
                  <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: invoiceColor, marginBottom: 4 }}>{p.label}</div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name || '—'}</div>
                  {p.addr && <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>{p.addr}</div>}
                  {p.email && <div style={{ fontSize: 11, color: '#555' }}>{p.email}</div>}
                  {p.phone && <div style={{ fontSize: 11, color: '#555' }}>{p.phone}</div>}
                </div>
              ))}
            </div>

            {/* Tableau articles */}
            <div style={{ margin: '10px 28px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: `rgba(${r2},${g2},${b2},1)`, color: '#fff' }}>
                    {['Description', 'Qté', 'Prix unit.', 'Total'].map((h, i) => (
                      <th key={i} style={{ padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right', fontWeight: 700, fontSize: 11 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                      <td style={{ padding: '7px 10px' }}>{item.description || '—'}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right' }}>{item.quantity}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right' }}>{parseFloat(item.price).toFixed(2)} {s}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{(parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2)} {s}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totaux */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 28px 10px' }}>
              <div style={{ minWidth: 240 }}>
                {[
                  { label: 'Sous-total', val: `${subtotal.toFixed(2)} ${s}` },
                  { label: `TVA (${formData.taxRate}%)`, val: `${tax.toFixed(2)} ${s}` },
                ].map((row, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, color: '#555' }}>
                    <span>{row.label}</span><span style={{ fontWeight: 600, color: '#1a1a1a' }}>{row.val}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px', borderTop: `2px solid ${invoiceColor}`, marginTop: 4, fontSize: 15, fontWeight: 800, color: invoiceColor }}>
                  <span>Total TTC</span><span>{total.toFixed(2)} {s}</span>
                </div>
              </div>
            </div>

            {/* Mode paiement */}
            {invoicePaymentMethods.length > 0 && (
              <div style={{ margin: '0 28px 12px', padding: '8px 12px', background: `rgba(${r2},${g2},${b2},0.08)`, borderRadius: 8, border: `1px solid rgba(${r2},${g2},${b2},0.25)`, fontSize: 12, fontWeight: 600, color: invoiceColor, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-wallet"></i> Mode(s) de paiement : {invoicePaymentMethods.join('  |  ')}
              </div>
            )}

            {/* Notes */}
            {formData.notes && (
              <div style={{ margin: '0 28px 16px', padding: '10px 14px', borderLeft: `3px solid ${invoiceColor}`, background: '#f8fafc', fontSize: 11, color: '#4a5568' }}>
                <strong style={{ color: invoiceColor, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>Notes</strong>
                <div style={{ marginTop: 4 }}>{formData.notes}</div>
              </div>
            )}

            {/* Signatures */}
            {showInvoiceSig && (
              <div style={{ margin: '0 28px 28px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div style={{ border: `1px dashed rgba(${r2},${g2},${b2},0.5)`, borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: invoiceColor, textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><i className="fas fa-pen"></i> {formData.companyName || 'Prestataire'}</div>
                  {sigFacture
                    ? <img src={sigFacture} alt="sig" style={{ height: 50, objectFit: 'contain', width: '100%' }} />
                    : <div style={{ height: 50, borderBottom: '1px solid #ddd' }} />
                  }
                  <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>Signature</div>
                </div>
                <div style={{ border: `2px solid ${invoiceColor}`, borderRadius: 10, padding: 14, background: `rgba(${r2},${g2},${b2},0.04)` }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: invoiceColor, textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><i className="fas fa-check-circle"></i> {formData.clientName || 'Client'}</div>
                  <div style={{ height: 50, borderBottom: `1px solid rgba(${r2},${g2},${b2},0.3)` }} />
                  <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>Date : {new Date().toLocaleDateString('fr-FR')}</div>
                </div>
              </div>
            )}
          </div>
        );
      };

      ReactDOM.render(<SheetContent />, container);
      await new Promise(r => setTimeout(r, 300));

      const canvas = await html2canvas(container, {
        scale: 2, useCORS: true, allowTaint: true,
        backgroundColor: '#ffffff', logging: false,
      });

      document.body.removeChild(container);

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const imgW = pdfW;
      const imgH = (canvas.height * pdfW) / canvas.width;

      let heightLeft = imgH;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgW, imgH);
      heightLeft -= pdfH;

      while (heightLeft > 0) {
        position -= pdfH;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgW, imgH);
        heightLeft -= pdfH;
      }

      try {
        await axios.post('/pdf', {
          type: 'invoice',
          fileName: `${formData.invoiceNumber || 'facture'}.pdf`,
          data: { ...formData, color: invoiceColor },
        });
      } catch (err) { console.error(err.message); }

      pdf.save(`${formData.invoiceNumber || 'facture'}.pdf`);
      showNotif('Facture téléchargée ✓');
      incrementInvoiceNum();

    } catch (err) {
      console.error('Erreur PDF:', err);
      showNotif('Erreur lors de la génération du PDF', 'error');
    }
  };

  /* ── PDF Devis (complet) ── */
  const generateDevisPDF = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF: JsPDF } = await import('jspdf');

      const container = document.createElement('div');
      container.style.cssText = `position: fixed; top: -9999px; left: -9999px; width: 794px; background: white; z-index: -1;`;
      document.body.appendChild(container);

      const ReactDOM = await import('react-dom');
      const [r2, g2, b2] = hexToRgb(devisColor);
      const sym2 = sym;
      const subtotal = calcDevisSubtotal();
      const tax2 = calcDevisTax();
      const total = calcDevisTotal();
      const sc = { Brouillon: '#6b7280', Envoyé: '#3b82f6', Accepté: '#10b981', Refusé: '#f43f5e' };
      const statutColor = sc[devisData.statut] || '#6b7280';

      const Sheet = () => (
        <div style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 13, color: '#1a1a1a', background: '#fff' }}>
          {/* Bandeau */}
          <div style={{ background: `linear-gradient(135deg, rgb(${r2},${g2},${b2}), rgba(${r2},${g2},${b2},0.85))`, color: '#fff', padding: '20px 28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {devisData.companyLogo && <img src={devisData.companyLogo} alt="logo" style={{ height: 48, maxWidth: 110, objectFit: 'contain', background: '#fff', borderRadius: 6, padding: 4 }} />}
                <div>
                  <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: 2 }}>DEVIS</div>
                  {devisData.objet && <div style={{ fontSize: 12, opacity: 0.9 }}>{devisData.objet}</div>}
                </div>
              </div>
              <span style={{ background: `${statutColor}30`, color: '#fff', border: `1px solid rgba(255,255,255,0.4)`, padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                {devisData.statut}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: 11, opacity: 0.92 }}>
              {devisData.devisNumber && <span>N° <strong>{devisData.devisNumber}</strong></span>}
              <span>Émis le : <strong>{devisData.devisDate}</strong></span>
              {devisData.validUntil && <span><i className="fas fa-clock" style={{ marginRight: '4px' }}></i> Valable jusqu'au <strong>{devisData.validUntil}</strong></span>}
            </div>
          </div>

          {/* Parties */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, padding: '20px 28px 10px' }}>
            {[{ label: 'DE', name: devisData.companyName, addr: devisData.companyAddress, email: devisData.companyEmail },
            { label: 'À', name: devisData.clientName, addr: devisData.clientAddress, email: devisData.clientEmail }
            ].map((p, i) => (
              <div key={i}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: devisColor, marginBottom: 4 }}>{p.label}</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name || '—'}</div>
                {p.addr && <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>{p.addr}</div>}
                {p.email && <div style={{ fontSize: 11, color: '#555' }}>{p.email}</div>}
              </div>
            ))}
          </div>

          {/* Tableau */}
          <div style={{ margin: '10px 28px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: `rgb(${r2},${g2},${b2})`, color: '#fff' }}>
                  {['Description', 'Qté', 'Prix unit.', 'Total'].map((h, i) => (
                    <th key={i} style={{ padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right', fontWeight: 700, fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {devisData.items.map((item, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                    <td style={{ padding: '7px 10px' }}>{item.description || '—'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{item.quantity}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{parseFloat(item.price).toFixed(2)} {sym2}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{(parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2)} {sym2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totaux */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 28px 10px' }}>
            <div style={{ minWidth: 240 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, color: '#555' }}>
                <span>Sous-total</span><span style={{ fontWeight: 600, color: '#1a1a1a' }}>{subtotal.toFixed(2)} {sym2}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, color: '#555' }}>
                <span>TVA ({devisData.taxRate}%)</span><span style={{ fontWeight: 600, color: '#1a1a1a' }}>{tax2.toFixed(2)} {sym2}</span>
              </div>
              {devisData.acompte > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, color: '#f97316' }}>
                  <span>Acompte ({devisData.acompte}%)</span>
                  <span style={{ fontWeight: 600 }}>{(total * devisData.acompte / 100).toFixed(2)} {sym2}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px', borderTop: `2px solid ${devisColor}`, marginTop: 4, fontSize: 15, fontWeight: 800, color: devisColor }}>
                <span>Total TTC</span><span>{total.toFixed(2)} {sym2}</span>
              </div>
            </div>
          </div>

          {/* Mode paiement */}
          {devisPaymentMethods.length > 0 && (
            <div style={{ margin: '0 28px 12px', padding: '8px 12px', background: `rgba(${r2},${g2},${b2},0.08)`, borderRadius: 8, border: `1px solid rgba(${r2},${g2},${b2},0.25)`, fontSize: 12, fontWeight: 600, color: devisColor, display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-wallet"></i> Mode(s) de paiement : {devisPaymentMethods.join('  |  ')}
            </div>
          )}

          {/* Options facultatives */}
          {devisOptions?.length > 0 && (
            <div style={{ margin: '0 28px 12px' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: devisColor, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Options facultatives</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, opacity: 0.85 }}>
                <thead>
                  <tr style={{ background: `rgba(${r2},${g2},${b2},0.08)` }}>
                    <th style={{ padding: '5px 8px', textAlign: 'left' }}>Option</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '5px 8px', textAlign: 'right' }}>Prix</th>
                  </tr>
                </thead>
                <tbody>
                  {devisOptions.map((opt, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '5px 8px', fontWeight: 600 }}>
                        <span style={{ fontSize: 9, background: `${devisColor}15`, color: devisColor, padding: '1px 6px', borderRadius: 10, marginRight: 4, fontWeight: 700 }}>OPT</span>
                        {opt.label}
                      </td>
                      <td style={{ padding: '5px 8px', color: '#888' }}>{opt.description}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: devisColor }}>{parseFloat(opt.price || 0).toFixed(2)} {sym2}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 9, color: '#999', fontStyle: 'italic', marginTop: 4 }}>* Options non incluses dans le montant total.</div>
            </div>
          )}

          {/* Notes */}
          {devisData.notes && (
            <div style={{ margin: '0 28px 16px', padding: '10px 14px', borderLeft: `3px solid ${devisColor}`, background: '#f8fafc', fontSize: 11, color: '#4a5568' }}>
              <strong style={{ color: devisColor, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>Conditions</strong>
              <div style={{ marginTop: 4 }}>{devisData.notes}</div>
            </div>
          )}

          {/* Signatures */}
          {showDevisSig && (
            <div style={{ margin: '0 28px 28px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div style={{ border: `1px dashed rgba(${r2},${g2},${b2},0.5)`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: devisColor, textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><i className="fas fa-pen"></i> {devisData.companyName || 'Prestataire'}</div>
                {sigPrestataire
                  ? <img src={sigPrestataire} alt="sig" style={{ height: 50, objectFit: 'contain', width: '100%' }} />
                  : <div style={{ height: 50, borderBottom: '1px solid #ddd' }} />
                }
                <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>Signature</div>
              </div>
              <div style={{ border: `2px solid ${devisColor}`, borderRadius: 10, padding: 14, background: `rgba(${r2},${g2},${b2},0.04)` }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: devisColor, textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><i className="fas fa-check-circle"></i> {devisData.clientName || 'Client'}</div>
                <div style={{ height: 50, borderBottom: `1px solid rgba(${r2},${g2},${b2},0.3)` }} />
                <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>Date : {new Date().toLocaleDateString('fr-FR')}</div>
              </div>
            </div>
          )}
        </div>
      );

      ReactDOM.render(<Sheet />, container);
      await new Promise(r => setTimeout(r, 300));

      const canvas = await html2canvas(container, {
        scale: 2, useCORS: true, allowTaint: true,
        backgroundColor: '#ffffff', logging: false,
      });

      document.body.removeChild(container);

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pdfW) / canvas.width;

      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(imgData, 'JPEG', 0, position, pdfW, imgH);
      heightLeft -= pdfH;

      while (heightLeft > 0) {
        position -= pdfH;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, pdfW, imgH);
        heightLeft -= pdfH;
      }

      try {
        await axios.post('/pdf', {
          type: 'quote',
          fileName: `${devisData.devisNumber || 'devis'}.pdf`,
          data: { ...devisData, color: devisColor },
        });
      } catch (err) { console.error(err.message); }

      pdf.save(`${devisData.devisNumber || 'devis'}.pdf`);
      showNotif('Devis téléchargé ✓');
      incrementDevisNum();

    } catch (err) {
      console.error('Erreur PDF devis:', err);
      showNotif('Erreur lors de la génération du PDF', 'error');
    }
  };

  /* ── Computed ── */
  const filteredClients = clients.filter(c => {
    const m = c.name.toLowerCase().includes(clientSearch.toLowerCase()) || (c.email || '').toLowerCase().includes(clientSearch.toLowerCase());
    return m && (clientFilter === 'all' || c.type === clientFilter);
  });
  const particuliers = clients.filter(c => c.type === 'particulier');
  const societes = clients.filter(c => c.type === 'societe');

  const kanbanCols = [
    { label: 'Prospect', pct: '10%', color: '#a78bfa', bg: 'rgba(167,139,250,0.08)' },
    { label: 'Contacté', pct: '30%', color: '#f97316', bg: 'rgba(249,115,22,0.08)' },
    { label: 'Proposition', pct: '50%', color: '#eab308', bg: 'rgba(234,179,8,0.08)' },
    { label: 'Négociation', pct: '70%', color: '#3b82f6', bg: 'rgba(59,130,246,0.08)' },
    { label: 'Clôture', pct: '90%', color: '#10b981', bg: 'rgba(16,185,129,0.08)' },
  ];

  /* ══════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════ */
  return (
    <div className="page-with-sidebar">
      <Sidebar />

      {notification && (
        <Notification type={notification.type} message={notification.msg} onClose={() => setNotification(null)} />
      )}

      <main className="invoice-main" style={{ marginLeft: 'var(--sidebar-width)', width: 'calc(100vw - var(--sidebar-width))', minHeight: '100vh', background: 'var(--bg-page)', boxSizing: 'border-box', padding: '2rem 3rem', overflow: 'hidden' }}>

        <div className="invoice-header">
          <div>
            <h1><i className="fas fa-file-invoice" style={{ color: 'var(--accent-start)' }}></i> {tr('facturation')}</h1>
            <p>{tr('choisir_type')}</p>
          </div>
        </div>

        {/* TABS */}
        <div className="inv-tabs">
          {[
            { id: 'facture', icon: 'fa-file-invoice', label: tr('facture') },
            { id: 'devis', icon: 'fa-file-contract', label: 'Devis' },
            { id: 'historique', icon: 'fa-history', label: 'Historique' },
            { id: 'clients', icon: 'fa-users', label: `${tr('clients')} (${clients.length})` },
            { id: 'crm', icon: 'fa-handshake', label: tr('crm') },
          ].map(tab => (
            <button key={tab.id} className={`inv-tab ${activeTab === tab.id ? 'inv-tab-active' : ''}`} onClick={() => setActiveTab(tab.id)}>
              <i className={`fas ${tab.icon}`}></i> {tab.label}
            </button>
          ))}
        </div>

        {/* ══ FACTURE ══ */}
        {activeTab === 'facture' && (
          <div className="invoice-container">
            <div className="invoice-form">
              <div className="card">

                {/* Couleur + Logo */}
                <ColorLogoSection
                  color={invoiceColor}
                  onColorChange={setInvoiceColor}
                  logo={formData.companyLogo}
                  onLogoChange={logo => setFormData(p => ({ ...p, companyLogo: logo }))}
                  onLogoRemove={() => setFormData(p => ({ ...p, companyLogo: null }))}
                />

                {/* Entreprise */}
                <div className="form-section">
                  <h3><i className="fas fa-building"></i> {tr('votre_entreprise')}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{tr('nom_entreprise')} *</label>
                      <input type="text" name="companyName" className="form-input" value={formData.companyName} onChange={handleChange} placeholder="FlowKraft Agency" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('email')}</label>
                      <input type="email" name="companyEmail" className="form-input" value={formData.companyEmail} onChange={handleChange} placeholder="contact@flowkraft.com" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{tr('adresse')}</label>
                      <input type="text" name="companyAddress" className="form-input" value={formData.companyAddress} onChange={handleChange} placeholder="123 rue de la Paix, Lomé" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('telephone')}</label>
                      <input type="tel" name="companyPhone" className="form-input" value={formData.companyPhone} onChange={handleChange} placeholder="+228 99 00 00 00" />
                    </div>
                  </div>
                </div>

                {/* Client */}
                <div className="form-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h3 style={{ margin: 0 }}><i className="fas fa-user"></i> {tr('infos_client')}</h3>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleSaveClientFromForm('facture')}>
                        <i className="fas fa-user-plus"></i> Sauvegarder client
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setActiveTab('clients')}>
                        <i className="fas fa-address-book"></i> {tr('choisir_client')}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('nom_client')} *</label>
                    <ClientSelector
                      value={formData.clientName}
                      clients={clients}
                      onChange={val => setFormData(p => ({ ...p, clientName: val }))}
                      onSelect={c => setFormData(p => ({
                        ...p,
                        clientName: c.name,
                        clientEmail: c.email || '',
                        clientPhone: c.phone || '',
                        clientAddress: `${c.address || ''} ${c.city || ''} ${c.country || ''}`.trim(),
                      }))}
                      placeholder="Rechercher ou saisir un client..."
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{tr('email')}</label>
                      <input type="email" name="clientEmail" className="form-input" value={formData.clientEmail} onChange={handleChange} placeholder="client@email.fr" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('telephone')}</label>
                      <input type="tel" name="clientPhone" className="form-input" value={formData.clientPhone} onChange={handleChange} placeholder="+228 99 00 00 00" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('adresse')}</label>
                    <input type="text" name="clientAddress" className="form-input" value={formData.clientAddress} onChange={handleChange} placeholder="456 Avenue des Champs" />
                  </div>
                </div>

                {/* Détails facture */}
                <div className="form-section">
                  <h3><i className="fas fa-file-alt"></i> {tr('details_facture')}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{tr('numero_facture')}</label>
                      <input type="text" name="invoiceNumber" className="form-input" value={formData.invoiceNumber} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('date_emission')}</label>
                      <input type="date" name="invoiceDate" className="form-input" value={formData.invoiceDate} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('date_echeance')}</label>
                      <input type="date" name="dueDate" className="form-input" value={formData.dueDate} onChange={handleChange} />
                    </div>
                  </div>
                  {isPaid && (
                    <div className="form-row inv-paid-fields">
                      <div className="form-group">
                        <label className="form-label"><i className="fas fa-calendar-check"></i> Date de paiement</label>
                        <input type="date" name="paidDate" className="form-input" value={formData.paidDate} onChange={handleChange} />
                      </div>
                      <div className="form-group">
                        <label className="form-label"><i className="fas fa-credit-card"></i> Moyen de paiement</label>
                        <select name="paymentMethod" className="form-input" value={formData.paymentMethod} onChange={handleChange}>
                          <option value="">— Sélectionner —</option>
                          <option>Virement bancaire</option><option>Carte bancaire</option><option>Chèque</option>
                          <option>Espèces</option><option>PayPal</option><option>Mobile Money</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Articles */}
                <div className="form-section">
                  <h3><i className="fas fa-list"></i> {tr('articles')}</h3>
                  {formData.items.map((item, index) => (
                    <div key={index} className="item-row">
                      <div className="item-header">
                        <span className="item-number">Article #{index + 1}</span>
                        {formData.items.length > 1 && <button type="button" className="btn-remove" onClick={() => removeItem(index)}><i className="fas fa-trash"></i></button>}
                      </div>
                      <div className="item-fields">
                        <div className="form-group" style={{ flex: 2 }}>
                          <label className="form-label">{tr('description')}</label>
                          <input type="text" className="form-input" value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} placeholder="Description du service" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{tr('quantite')}</label>
                          <input type="number" className="form-input" value={item.quantity} onChange={e => handleItemChange(index, 'quantity', e.target.value)} min="1" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{tr('prix_unit')} ({sym})</label>
                          <input type="number" className="form-input" value={item.price} onChange={e => handleItemChange(index, 'price', e.target.value)} min="0" step="0.01" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{tr('total')}</label>
                          <div className="item-total">{(parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2)} {sym}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addItem}>
                    <i className="fas fa-plus"></i> {tr('ajouter_article')}
                  </button>
                </div>

                <PaymentSelector
                  values={invoicePaymentMethods}
                  onChange={setInvoicePaymentMethods}
                  color={invoiceColor}
                />

                {/* Toggle signature optionnelle */}
                <div className="form-section">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', cursor: 'pointer', userSelect: 'none' }}>
                    <div
                      onClick={() => setShowInvoiceSig(v => !v)}
                      style={{
                        width: 44, height: 24, borderRadius: 12, position: 'relative',
                        background: showInvoiceSig ? invoiceColor : 'var(--border)',
                        transition: 'background 0.25s', flexShrink: 0, cursor: 'pointer',
                      }}
                    >
                      <div style={{
                        position: 'absolute', top: 3, left: showInvoiceSig ? 22 : 3,
                        width: 18, height: 18, borderRadius: '50%', background: '#fff',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 0.25s',
                      }} />
                    </div>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                      <i className="fas fa-signature" style={{ marginRight: '0.4rem', color: showInvoiceSig ? invoiceColor : 'var(--text-secondary)' }}></i>
                      Ajouter une zone de signature
                    </span>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>facultatif</span>
                  </label>
                </div>

                <div className="form-section">
                  <div className="form-row">
                    <div className="form-group" style={{ maxWidth: '180px' }}>
                      <label className="form-label">{tr('tva')} (%)</label>
                      <input type="number" name="taxRate" className="form-input" value={formData.taxRate} onChange={handleChange} min="0" max="100" step="0.1" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('notes')}</label>
                    <textarea name="notes" className="form-textarea" rows="3" value={formData.notes} onChange={handleChange} placeholder="Paiement sous 30 jours. IBAN : FR76..." />
                  </div>
                </div>

                <div className="invoice-totals">
                  <div className="total-row"><span>{tr('sous_total')} :</span><span>{calculateSubtotal().toFixed(2)} {sym}</span></div>
                  <div className="total-row"><span>TVA ({formData.taxRate}%) :</span><span>{calculateTax().toFixed(2)} {sym}</span></div>
                  <div className="total-row total-final"><span>{tr('total_ttc')} :</span><span style={{ color: invoiceColor }}>{calculateTotal().toFixed(2)} {sym}</span></div>
                </div>

                <div className="invoice-actions">
                  {editingInvoiceId && (
                    <span style={{ fontSize: '0.8rem', color: '#f97316', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <i className="fas fa-edit"></i> Mode édition
                    </span>
                  )}
                  <button className="btn-invoice-secondary" onClick={() => setIsPaid(v => !v)}>
                    <i className={`fas ${isPaid ? 'fa-times' : 'fa-check'}`}></i>
                    {isPaid ? tr('marquer_impayee') : tr('marquer_payee')}
                  </button>
                  <button className="btn-invoice-secondary" onClick={() => setShowPreview(true)}>
                    <i className="fas fa-eye"></i> {tr('apercu')}
                  </button>
                  <button className="btn-invoice-primary" style={{ background: `linear-gradient(135deg,${invoiceColor},${invoiceColor}cc)`, border: 'none' }} onClick={generatePDF}>
                    <i className="fas fa-file-pdf"></i> {tr('telecharger_pdf')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ DEVIS ══ */}
        {activeTab === 'devis' && (
          <div className="invoice-container">
            <div className="invoice-form">
              <div className="card">

                {/* Statut */}
                <div className="form-section">
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0 }}><i className="fas fa-tag" style={{ color: devisColor }}></i> Statut du devis</h3>
                    {['Brouillon', 'Envoyé', 'Accepté', 'Refusé'].map(s => {
                      const colors = { Brouillon: '#6b7280', Envoyé: '#3b82f6', Accepté: '#10b981', Refusé: '#f43f5e' };
                      return (
                        <button key={s} onClick={() => setDevisData(p => ({ ...p, statut: s }))} style={{
                          padding: '0.35rem 1rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 600,
                          cursor: 'pointer', border: 'none',
                          background: devisData.statut === s ? colors[s] : `${colors[s]}20`,
                          color: devisData.statut === s ? '#fff' : colors[s], transition: 'all 0.2s',
                        }}>{s}</button>
                      );
                    })}
                  </div>
                </div>

                {/* Couleur + Logo */}
                <ColorLogoSection
                  color={devisColor}
                  onColorChange={setDevisColor}
                  logo={devisData.companyLogo}
                  onLogoChange={logo => setDevisData(p => ({ ...p, companyLogo: logo }))}
                  onLogoRemove={() => setDevisData(p => ({ ...p, companyLogo: null }))}
                />

                {/* Émetteur */}
                <div className="form-section">
                  <h3><i className="fas fa-building"></i> {tr('votre_entreprise')}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{tr('nom_entreprise')} *</label>
                      <input type="text" name="companyName" className="form-input" value={devisData.companyName} onChange={handleDevisChange} placeholder="FlowKraft Agency" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('email')}</label>
                      <input type="email" name="companyEmail" className="form-input" value={devisData.companyEmail} onChange={handleDevisChange} placeholder="contact@flowkraft.com" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('adresse')}</label>
                    <input type="text" name="companyAddress" className="form-input" value={devisData.companyAddress} onChange={handleDevisChange} placeholder="123 rue de la Paix, Lomé" />
                  </div>
                </div>

                {/* Client */}
                <div className="form-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h3 style={{ margin: 0 }}><i className="fas fa-user"></i> {tr('infos_client')}</h3>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleSaveClientFromForm('devis')}>
                        <i className="fas fa-user-plus"></i> Sauvegarder client
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setActiveTab('clients')}>
                        <i className="fas fa-address-book"></i> {tr('choisir_client')}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('nom_client')} *</label>
                    <ClientSelector
                      value={devisData.clientName}
                      clients={clients}
                      onChange={val => setDevisData(p => ({ ...p, clientName: val }))}
                      onSelect={c => setDevisData(p => ({
                        ...p,
                        clientName: c.name,
                        clientEmail: c.email || '',
                        clientAddress: `${c.address || ''} ${c.city || ''} ${c.country || ''}`.trim(),
                      }))}
                      placeholder="Rechercher ou saisir un client..."
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{tr('email')}</label>
                      <input type="email" name="clientEmail" className="form-input" value={devisData.clientEmail} onChange={handleDevisChange} placeholder="client@email.fr" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('adresse')}</label>
                      <input type="text" name="clientAddress" className="form-input" value={devisData.clientAddress} onChange={handleDevisChange} placeholder="456 Avenue des Champs" />
                    </div>
                  </div>
                </div>

                {/* Détails devis */}
                <div className="form-section">
                  <h3><i className="fas fa-file-contract" style={{ color: devisColor }}></i> Détails du devis</h3>
                  <div className="form-group">
                    <label className="form-label">Objet du devis</label>
                    <input type="text" name="objet" className="form-input" value={devisData.objet} onChange={handleDevisChange} placeholder="Ex: Développement site e-commerce..." />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">N° de devis</label>
                      <input type="text" name="devisNumber" className="form-input" value={devisData.devisNumber} onChange={handleDevisChange} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Date d'émission</label>
                      <input type="date" name="devisDate" className="form-input" value={devisData.devisDate} onChange={handleDevisChange} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" style={{ color: '#f97316' }}><i className="fas fa-clock" style={{ marginRight: '0.3rem' }}></i> Valable jusqu'au</label>
                      <input type="date" name="validUntil" className="form-input" value={devisData.validUntil} onChange={handleDevisChange} style={{ borderColor: devisData.validUntil ? '#f97316' : undefined }} />
                    </div>
                  </div>
                </div>

                {/* Détails de la prestation */}
                <div className="form-section">
                  <h3><i className="fas fa-align-left" style={{ color: devisColor }}></i> Détails de la prestation</h3>
                  <div className="form-group">
                    <label className="form-label">Description complète</label>
                    <textarea
                      name="details"
                      className="form-textarea"
                      rows={4}
                      value={devisData.details}
                      onChange={handleDevisChange}
                      placeholder="Décrivez en détail la prestation proposée : périmètre, livrables, délais, méthodologie..."
                      style={{ minHeight: 110 }}
                    />
                  </div>
                </div>

                <PaymentSelector
                  values={devisPaymentMethods}
                  onChange={setDevisPaymentMethods}
                  color={devisColor}
                />

                <div className="form-section">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', cursor: 'pointer', userSelect: 'none' }}>
                    <div
                      onClick={() => setShowDevisSig(v => !v)}
                      style={{
                        width: 44, height: 24, borderRadius: 12, position: 'relative',
                        background: showDevisSig ? devisColor : 'var(--border)',
                        transition: 'background 0.25s', flexShrink: 0, cursor: 'pointer',
                      }}
                    >
                      <div style={{
                        position: 'absolute', top: 3, left: showDevisSig ? 22 : 3,
                        width: 18, height: 18, borderRadius: '50%', background: '#fff',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 0.25s',
                      }} />
                    </div>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                      <i className="fas fa-signature" style={{ marginRight: '0.4rem', color: showDevisSig ? devisColor : 'var(--text-secondary)' }}></i>
                      Ajouter une zone de signature
                    </span>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>facultatif</span>
                  </label>
                </div>

                {/* Options facultatives */}
                <div className="form-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h3 style={{ margin: 0 }}>
                      <i className="fas fa-puzzle-piece" style={{ color: devisColor }}></i> Options facultatives
                      <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                        (non incluses dans le total)
                      </span>
                    </h3>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setDevisOptions(prev => [...prev, { id: Date.now(), label: '', price: 0, description: '' }])}
                    >
                      <i className="fas fa-plus"></i> Ajouter une option
                    </button>
                  </div>

                  {devisOptions.length === 0 ? (
                    <div style={{
                      border: '2px dashed var(--border)', borderRadius: 'var(--border-radius-lg)',
                      padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem',
                    }}>
                      <i className="fas fa-puzzle-piece" style={{ fontSize: '1.5rem', opacity: 0.3, display: 'block', marginBottom: '0.5rem' }}></i>
                      Aucune option — cliquez sur "Ajouter une option" pour en créer
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                      {devisOptions.map((opt, idx) => (
                        <div key={opt.id} style={{
                          background: `${devisColor}06`,
                          border: `1px solid ${devisColor}30`,
                          borderRadius: 'var(--border-radius-lg)',
                          padding: '1.1rem',
                          position: 'relative',
                        }}>
                          {/* Badge */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
                            <span style={{
                              fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                              color: devisColor, background: `${devisColor}15`,
                              padding: '0.2rem 0.65rem', borderRadius: '20px',
                              display: 'flex', alignItems: 'center', gap: '0.35rem',
                            }}>
                              <i className="fas fa-toggle-on"></i> Option #{idx + 1} — Facultative
                            </span>
                            <button
                              type="button"
                              onClick={() => setDevisOptions(prev => prev.filter(o => o.id !== opt.id))}
                              style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', color: '#ef4444', fontSize: '0.75rem' }}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>

                          <div className="form-row" style={{ gridTemplateColumns: '2fr 1fr', gap: '0.85rem', marginBottom: '0.75rem' }}>
                            <div className="form-group">
                              <label className="form-label">Intitulé de l'option *</label>
                              <input
                                type="text"
                                className="form-input"
                                value={opt.label}
                                onChange={e => setDevisOptions(prev => prev.map(o => o.id === opt.id ? { ...o, label: e.target.value } : o))}
                                placeholder="Ex: Support technique 3 mois, Formation utilisateurs..."
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Prix ({sym})</label>
                              <input
                                type="number"
                                className="form-input"
                                value={opt.price}
                                min="0"
                                step="0.01"
                                onChange={e => setDevisOptions(prev => prev.map(o => o.id === opt.id ? { ...o, price: e.target.value } : o))}
                                placeholder="0.00"
                              />
                            </div>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Description (facultatif)</label>
                            <input
                              type="text"
                              className="form-input"
                              value={opt.description}
                              onChange={e => setDevisOptions(prev => prev.map(o => o.id === opt.id ? { ...o, description: e.target.value } : o))}
                              placeholder="Brève description de cette option..."
                            />
                          </div>

                          {opt.price > 0 && (
                            <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: devisColor, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                              <i className="fas fa-info-circle"></i>
                              Option à {parseFloat(opt.price).toFixed(2)} {sym} — non incluse dans le total du devis
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Articles */}
                <div className="form-section">
                  <h3><i className="fas fa-list"></i> {tr('articles')}</h3>
                  {devisData.items.map((item, index) => (
                    <div key={index} className="item-row">
                      <div className="item-header">
                        <span className="item-number">Article #{index + 1}</span>
                        {devisData.items.length > 1 && <button type="button" className="btn-remove" onClick={() => removeDevisItem(index)}><i className="fas fa-trash"></i></button>}
                      </div>
                      <div className="item-fields">
                        <div className="form-group" style={{ flex: 2 }}>
                          <label className="form-label">{tr('description')}</label>
                          <input type="text" className="form-input" value={item.description} onChange={e => handleDevisItemChange(index, 'description', e.target.value)} placeholder="Description du service" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{tr('quantite')}</label>
                          <input type="number" className="form-input" value={item.quantity} onChange={e => handleDevisItemChange(index, 'quantity', e.target.value)} min="1" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{tr('prix_unit')} ({sym})</label>
                          <input type="number" className="form-input" value={item.price} onChange={e => handleDevisItemChange(index, 'price', e.target.value)} min="0" step="0.01" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{tr('total')}</label>
                          <div className="item-total">{(parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2)} {sym}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addDevisItem}>
                    <i className="fas fa-plus"></i> {tr('ajouter_article')}
                  </button>
                </div>

                {/* TVA + Acompte */}
                <div className="form-section">
                  <div className="form-row">
                    <div className="form-group" style={{ maxWidth: '150px' }}>
                      <label className="form-label">{tr('tva')} (%)</label>
                      <input type="number" name="taxRate" className="form-input" value={devisData.taxRate} onChange={handleDevisChange} min="0" max="100" step="0.1" />
                    </div>
                    <div className="form-group" style={{ maxWidth: '180px' }}>
                      <label className="form-label" style={{ color: '#f97316' }}><i className="fas fa-hand-holding-usd"></i> Acompte demandé (%)</label>
                      <input type="number" name="acompte" className="form-input" value={devisData.acompte} onChange={handleDevisChange} min="0" max="100" step="5" placeholder="0" style={{ borderColor: devisData.acompte > 0 ? '#f97316' : undefined }} />
                    </div>
                    {devisData.acompte > 0 && (
                      <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                        <div style={{ background: '#f9731615', border: '1px solid #f9731640', borderRadius: '10px', padding: '0.6rem 1rem', fontSize: '0.88rem', color: '#f97316', fontWeight: 600 }}>
                          Acompte = {(calcDevisTotal() * devisData.acompte / 100).toFixed(2)} {sym}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Conditions générales / Notes</label>
                    <textarea name="notes" className="form-textarea" rows="3" value={devisData.notes} onChange={handleDevisChange} placeholder="Devis valable 30 jours. Acompte de 30% à la signature..." />
                  </div>
                </div>

                <div className="invoice-totals" style={{ borderTop: `2px solid ${devisColor}30` }}>
                  <div className="total-row"><span>{tr('sous_total')} :</span><span>{calcDevisSubtotal().toFixed(2)} {sym}</span></div>
                  <div className="total-row"><span>TVA ({devisData.taxRate}%) :</span><span>{calcDevisTax().toFixed(2)} {sym}</span></div>
                  {devisData.acompte > 0 && (
                    <div className="total-row" style={{ color: '#f97316' }}>
                      <span>Acompte ({devisData.acompte}%) :</span>
                      <span>{(calcDevisTotal() * devisData.acompte / 100).toFixed(2)} {sym}</span>
                    </div>
                  )}
                  <div className="total-row total-final" style={{ color: devisColor }}>
                    <span>{tr('total_ttc')} :</span><span>{calcDevisTotal().toFixed(2)} {sym}</span>
                  </div>
                </div>

                <div className="invoice-actions">
                  {devisData.statut === 'Accepté' && (
                    <button className="btn-invoice-secondary" onClick={handleConvertToInvoice} style={{ color: '#667eea', borderColor: '#667eea' }}>
                      <i className="fas fa-file-invoice"></i> Convertir en facture
                    </button>
                  )}
                  <button className="btn-invoice-secondary" onClick={() => setShowDevisPreview(true)}>
                    <i className="fas fa-eye"></i> {tr('apercu')}
                  </button>
                  <button className="btn-invoice-primary" style={{ background: `linear-gradient(135deg,${devisColor},${devisColor}cc)`, border: 'none' }} onClick={generateDevisPDF}>
                    <i className="fas fa-file-pdf"></i> Télécharger le devis
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ HISTORIQUE ══ */}
        {activeTab === 'historique' && (
          <div className="invoice-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-primary)' }}>
                  <i className="fas fa-history" style={{ color: 'var(--accent-start)', marginRight: '0.5rem' }}></i>
                  Documents enregistrés
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0.25rem 0 0' }}>
                  Factures et devis — cliquez sur "Éditer" pour recharger et modifier
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Compteurs */}
                {savedInvoices.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', background: 'rgba(102,126,234,0.1)', color: 'var(--accent-start)', padding: '0.25rem 0.75rem', borderRadius: '20px', fontWeight: 600 }}>
                      <i className="fas fa-file-invoice" style={{ marginRight: '4px' }}></i>
                      {savedInvoices.filter(d => d.type === 'invoice').length} facture{savedInvoices.filter(d => d.type === 'invoice').length > 1 ? 's' : ''}
                    </span>
                    <span style={{ fontSize: '0.8rem', background: 'rgba(16,185,129,0.1)', color: '#10b981', padding: '0.25rem 0.75rem', borderRadius: '20px', fontWeight: 600 }}>
                      <i className="fas fa-file-contract" style={{ marginRight: '4px' }}></i>
                      {savedInvoices.filter(d => d.type === 'quote').length} devis
                    </span>
                  </div>
                )}
                <button className="btn btn-secondary btn-sm" onClick={loadSavedInvoices}>
                  <i className="fas fa-sync-alt"></i> Actualiser
                </button>
              </div>
            </div>

            {savedInvoicesLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}></i>
                Chargement...
              </div>
            ) : savedInvoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-secondary)', background: 'var(--bg-card)', borderRadius: 'var(--border-radius-xl)', border: '1px solid var(--border)' }}>
                <i className="fas fa-folder-open" style={{ fontSize: '3rem', opacity: 0.2, display: 'block', marginBottom: '1rem' }}></i>
                <p style={{ margin: 0 }}>Aucun document sauvegardé.</p>
                <p style={{ fontSize: '0.85rem', margin: '0.5rem 0 0' }}>Générez une facture ou un devis pour le voir ici.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                {savedInvoices.map(doc => {
                  const isInvoice = doc.type === 'invoice';
                  const data = (() => { try { return typeof doc.data === 'string' ? JSON.parse(doc.data) : doc.data; } catch { return {}; } })();
                  const accentColor = isInvoice ? 'var(--accent-start)' : '#10b981';
                  const accentBg    = isInvoice ? 'rgba(102,126,234,0.1)' : 'rgba(16,185,129,0.1)';
                  return (
                    <div key={doc.id}
                      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius-lg)', padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', transition: 'box-shadow 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-hover)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      {/* Icône */}
                      <div style={{ width: 46, height: 46, borderRadius: 'var(--border-radius-md)', background: isInvoice ? 'linear-gradient(135deg, var(--accent-start), var(--accent-end))' : 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.2rem', flexShrink: 0 }}>
                        <i className={`fas ${isInvoice ? 'fa-file-invoice' : 'fa-file-contract'}`}></i>
                      </div>

                      {/* Infos */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem', marginBottom: '0.3rem' }}>
                          {doc.fileName || (isInvoice ? 'Facture sans nom' : 'Devis sans nom')}
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.8rem', color: 'var(--text-secondary)', alignItems: 'center' }}>
                          {data.clientName && (
                            <span><i className="fas fa-user" style={{ marginRight: '4px', color: accentColor }}></i>{data.clientName}</span>
                          )}
                          {(data.invoiceNumber || data.devisNumber) && (
                            <span><i className="fas fa-hashtag" style={{ marginRight: '4px', color: accentColor }}></i>{data.invoiceNumber || data.devisNumber}</span>
                          )}
                          {(data.invoiceDate || data.devisDate) && (
                            <span><i className="fas fa-calendar" style={{ marginRight: '4px', color: accentColor }}></i>{new Date(data.invoiceDate || data.devisDate).toLocaleDateString('fr-FR')}</span>
                          )}
                          {!isInvoice && data.statut && (
                            <span style={{ padding: '0.1rem 0.5rem', borderRadius: '10px', fontWeight: 600, fontSize: '0.75rem', background: { Brouillon: '#6b728015', Envoyé: '#3b82f615', Accepté: '#10b98115', Refusé: '#f43f5e15' }[data.statut] || '#6b728015', color: { Brouillon: '#6b7280', Envoyé: '#3b82f6', Accepté: '#10b981', Refusé: '#f43f5e' }[data.statut] || '#6b7280' }}>
                              {data.statut}
                            </span>
                          )}
                          <span style={{ background: accentBg, color: accentColor, padding: '0.1rem 0.5rem', borderRadius: '10px', fontWeight: 600 }}>
                            {isInvoice ? 'Facture' : 'Devis'}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => isInvoice ? handleEditSavedInvoice(doc) : handleEditSavedDevis(doc)}
                        >
                          <i className="fas fa-edit"></i> Éditer
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ background: '#fee2e215', color: '#ef4444', border: '1px solid #ef444430' }}
                          onClick={async () => {
                            if (!window.confirm(`Supprimer ce${isInvoice ? 'tte facture' : ' devis'} ?`)) return;
                            try {
                              await axios.delete(`/pdf/${doc.id}`);
                              showNotif(`${isInvoice ? 'Facture' : 'Devis'} supprimé`);
                              loadSavedInvoices();
                            } catch { showNotif(tr('erreur_suppression'), 'error'); }
                          }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ CLIENTS ══ */}
        {activeTab === 'clients' && (
          <div className="invoice-container">
            <div className="clients-toolbar">
              <div className="clients-search-wrap">
                <i className="fas fa-search"></i>
                <input type="text" className="clients-search" placeholder={tr('rechercher_client')} value={clientSearch} onChange={e => setClientSearch(e.target.value)} />
              </div>
              <div className="clients-filter">
                {['all', 'particulier', 'societe'].map(f => (
                  <button key={f} className={`clients-filter-btn ${clientFilter === f ? 'active' : ''}`} onClick={() => setClientFilter(f)}>
                    {f === 'all' ? tr('tous') : f === 'particulier' ? tr('particuliers') : tr('societes')}
                  </button>
                ))}
              </div>
              <button className="btn-invoice-primary" onClick={() => { setEditingClient(null); setShowClientModal(true); }}>
                <i className="fas fa-plus"></i> {tr('nouveau_client')}
              </button>
            </div>

            <div className="clients-stats">
              <div className="clients-stat-card"><i className="fas fa-users"></i><div><span className="clients-stat-num">{clients.length}</span><span className="clients-stat-label">{tr('total_clients')}</span></div></div>
              <div className="clients-stat-card"><i className="fas fa-user"></i><div><span className="clients-stat-num">{particuliers.length}</span><span className="clients-stat-label">{tr('particuliers')}</span></div></div>
              <div className="clients-stat-card"><i className="fas fa-building"></i><div><span className="clients-stat-num">{societes.length}</span><span className="clients-stat-label">{tr('societes')}</span></div></div>
            </div>

            {clientsLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem' }}></i><p>{tr('chargement')}</p>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="clients-empty">
                <i className="fas fa-user-slash"></i>
                <h3>{tr('aucun_client')}</h3>
                <p>{tr('ajouter_client')}</p>
                <button className="btn-invoice-primary" onClick={() => { setEditingClient(null); setShowClientModal(true); }}>
                  <i className="fas fa-plus"></i> {tr('ajouter_client')}
                </button>
              </div>
            ) : (
              <div className="clients-grid">
                {filteredClients.map(client => (
                  <div key={client.id} className="client-card">
                    <div className="client-card-header">
                      <div className="client-avatar">
                        {client.type === 'societe'
                          ? <i className="fas fa-building"></i>
                          : client.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="client-card-info">
                        <h4>{client.name}</h4>
                        <span className={`client-type-badge ${client.type}`}>
                          {client.type === 'societe' ? tr('societes') : tr('particuliers')}
                        </span>
                      </div>

                      {/* Bouton Actions */}
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                          className="client-action-btn"
                          style={{ width: 'auto', padding: '0 0.75rem', gap: '0.35rem', display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: 600 }}
                          onClick={() => setOpenMenuId(openMenuId === client.id ? null : client.id)}
                        >
                          <i className="fas fa-ellipsis-v"></i>
                        </button>

                        {openMenuId === client.id && (
                          <>
                            {/* Overlay pour fermer */}
                            <div
                              style={{ position: 'fixed', inset: 0, zIndex: 100 }}
                              onClick={() => setOpenMenuId(null)}
                            />
                            {/* Menu */}
                            <div style={{
                              position: 'absolute', top: '110%', right: 0, zIndex: 200,
                              background: 'var(--bg-card)', border: '1px solid var(--border)',
                              borderRadius: 'var(--border-radius-lg)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                              minWidth: 180, overflow: 'hidden',
                              animation: 'fadeIn 0.15s ease',
                            }}>
                              {[
                                { icon: 'fa-file-invoice', label: tr('facture'), color: 'var(--accent-start)', action: () => { handleSelectClient(client); setOpenMenuId(null); } },
                                { icon: 'fa-file-contract', label: 'Devis', color: '#10b981', action: () => { handleSelectClientForDevis(client); setOpenMenuId(null); } },
                                { icon: 'fa-edit', label: tr('modifier'), color: 'var(--text-primary)', action: () => { setEditingClient(client); setShowClientModal(true); setOpenMenuId(null); } },
                                { icon: 'fa-trash', label: tr('supprimer'), color: '#ef4444', action: () => { handleDeleteClient(client.id); setOpenMenuId(null); }, danger: true },
                              ].map((item, i) => (
                                <button
                                  key={i}
                                  onClick={item.action}
                                  style={{
                                    width: '100%', display: 'flex', alignItems: 'center', gap: '0.65rem',
                                    padding: '0.7rem 1rem', border: 'none', background: 'transparent',
                                    cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
                                    color: item.danger ? '#ef4444' : 'var(--text-primary)',
                                    borderTop: i > 0 && item.danger ? '1px solid var(--border)' : 'none',
                                    transition: 'background 0.15s',
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = item.danger ? '#fee2e210' : 'var(--bg-subtle)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                  <i className={`fas ${item.icon}`} style={{ color: item.color, width: 16, textAlign: 'center' }}></i>
                                  {item.label}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="client-card-body">
                      {client.phone && <p><i className="fas fa-phone"></i> {client.phone}</p>}
                      {client.email && <p><i className="fas fa-envelope"></i> {client.email}</p>}
                      {client.city && <p><i className="fas fa-map-marker-alt"></i> {client.city}{client.country ? `, ${client.country}` : ''}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ CRM ══ */}
        {activeTab === 'crm' && (
          <div className="crm-layout">
            <aside className="crm-sidebar">
              <nav className="crm-sidebar-nav">
                <button className={`crm-nav-item ${crmSection === 'opportunities' ? 'crm-nav-active' : ''}`} onClick={() => setCrmSection('opportunities')}>
                  <i className="fas fa-handshake"></i><span>{tr('opportunites')}</span>
                  <button className="crm-nav-add" onClick={e => { e.stopPropagation(); setCrmSection('opportunities'); setCrmView('new-opportunity'); }}><i className="fas fa-plus"></i></button>
                </button>
                <button className={`crm-nav-item ${crmSection === 'activities' ? 'crm-nav-active' : ''}`} onClick={() => setCrmSection('activities')}>
                  <i className="fas fa-tasks"></i><span>{tr('activites')}</span>
                  <button className="crm-nav-add" onClick={e => { e.stopPropagation(); setCrmSection('activities'); setShowActivityModal(true); }}><i className="fas fa-plus"></i></button>
                </button>
              </nav>
            </aside>

            <div className="crm-main">
              {crmSection === 'opportunities' && (
                <div className="crm-kanban-wrapper">
                  <div className="crm-kanban-topbar">
                    <h2 className="crm-kanban-title">{tr('opportunites')}</h2>
                    <div className="crm-kanban-controls">
                      <div className="crm-kanban-search"><i className="fas fa-search"></i><input type="text" placeholder={tr('rechercher_client')} /></div>
                      <button className="crm-kanban-add-btn" onClick={() => setCrmView('new-opportunity')}><i className="fas fa-plus"></i> {tr('ajouter')}</button>
                    </div>
                  </div>
                  <div className="crm-kanban-tabs">
                    <button className="crm-kanban-tab crm-kanban-tab-active">{tr('tous_opp')}</button>
                    <button className="crm-kanban-tab">{tr('remportees')}</button>
                    <button className="crm-kanban-tab">{tr('perdues')}</button>
                  </div>
                  <div className="crm-kanban-board">
                    {kanbanCols.map(col => {
                      const colOpps = opportunities.filter(o => o.statut === col.label);
                      return (
                        <div key={col.label} className="crm-kanban-col">
                          <div className="crm-kanban-col-header" style={{ background: col.bg }}>
                            <i className="fas fa-circle" style={{ fontSize: '8px', color: col.color }}></i>
                            <span className="crm-kanban-col-name">{col.label}</span>
                            <span className="crm-kanban-col-pct" style={{ color: col.color }}>{col.pct}</span>
                            <span className="crm-kanban-col-count">{colOpps.length}</span>
                          </div>
                          <div className="crm-kanban-col-body">
                            {colOpps.map(opp => (
                              <div key={opp.id} className="crm-kanban-card">
                                <div className="crm-kanban-card-title">{opp.intitule}</div>
                                {opp.destinataire && <div className="crm-kanban-card-sub"><i className="fas fa-user" style={{ marginRight: '5px', opacity: 0.5 }}></i>{opp.destinataire}</div>}
                                {opp.montantHT && <div className="crm-kanban-card-amount">{parseFloat(opp.montantHT).toFixed(2)} {opp.devise === 'EUR' ? '€' : opp.devise}</div>}
                              </div>
                            ))}
                            <button className="crm-kanban-col-add" onClick={() => { setNewOpp(p => ({ ...p, statut: col.label })); setCrmView('new-opportunity'); }}>
                              <i className="fas fa-plus"></i>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {crmSection === 'activities' && (
                <div className="crm-activities-wrapper">
                  <div className="crm-kanban-topbar">
                    <h2 className="crm-kanban-title">{tr('activites')}</h2>
                    <div className="crm-kanban-controls">
                      <button className="crm-kanban-add-btn" onClick={() => setShowActivityModal(true)}><i className="fas fa-plus"></i> {tr('ajouter')}</button>
                      <button className="crm-act-export-btn" onClick={() => setShowExportModal(true)}><i className="fas fa-share-square"></i></button>
                    </div>
                  </div>
                  <div className="crm-kanban-tabs">
                    <button className={`crm-kanban-tab ${activityFilter === 'tous' ? 'crm-kanban-tab-active' : ''}`} onClick={() => setActivityFilter('tous')}>{tr('tous')}</button>
                  </div>
                  <div className="crm-act-counters">
                    {[
                      { key: 'aujourdhui', label: "Aujourd'hui", color: 'var(--accent-start)' },
                      { key: 'retard', label: 'En retard', color: '#ef4444' },
                      { key: 'avenir', label: 'À venir', color: '#6b7280' },
                      { key: 'terminee', label: 'Terminées', color: '#10b981' },
                    ].map(s => {
                      const count = activities.filter(a => getActivityStatus(a) === s.key).length;
                      return (
                        <div key={s.key} className={`crm-act-counter ${activityFilter === s.key ? 'crm-act-counter-active' : ''}`} style={{ '--act-color': s.color }} onClick={() => setActivityFilter(prev => prev === s.key ? 'tous' : s.key)}>
                          <span className="crm-act-counter-label">{s.label}</span>
                          <span className="crm-act-counter-num" style={{ color: s.color }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="crm-act-list">
                    {activities.filter(a => activityFilter === 'tous' || getActivityStatus(a) === activityFilter).length === 0 ? (
                      <div className="crm-empty-state"><i className="fas fa-tasks"></i><p>{tr('aucune_activite')}</p></div>
                    ) : (
                      activities.filter(a => activityFilter === 'tous' || getActivityStatus(a) === activityFilter)
                        .sort((a, b) => new Date(`${a.date}T${a.heure}`) - new Date(`${b.date}T${b.heure}`))
                        .map(act => {
                          const status = getActivityStatus(act);
                          const statusColors = { aujourdhui: 'var(--accent-start)', retard: '#ef4444', avenir: '#6b7280', terminee: '#10b981' };
                          return (
                            <div key={act.id} className={`crm-act-item ${act.realise ? 'crm-act-done' : ''}`}>
                              <button className={`crm-act-check ${act.realise ? 'crm-act-check-done' : ''}`} onClick={() => handleToggleActivity(act.id)}>
                                {act.realise && <i className="fas fa-check"></i>}
                              </button>
                              <div className="crm-act-content">
                                <span className="crm-act-title">{act.titre}</span>
                                <span className="crm-act-date" style={{ color: statusColors[status] }}>
                                  <i className="fas fa-clock" style={{ marginRight: '4px' }}></i>
                                  {new Date(`${act.date}T${act.heure}`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} à {act.heure}
                                </span>
                                {act.notes && <span className="crm-act-notes">{act.notes}</span>}
                              </div>
                              {act.responsable && <span className="crm-act-resp"><i className="fas fa-user" style={{ marginRight: '4px', opacity: 0.5 }}></i>{act.responsable}</span>}
                              <div className="crm-act-actions">
                                <button className="crm-act-menu-btn" onClick={() => handleDeleteActivity(act.id)}><i className="fas fa-trash-alt"></i></button>
                              </div>
                            </div>
                          );
                        })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Popup Nouvelle opportunité */}
            {crmView === 'new-opportunity' && (
              <div className="crm-opp-overlay" onClick={() => setCrmView('kanban')}>
                <div className="crm-opp-popup" onClick={e => e.stopPropagation()}>
                  <div className="crm-opp-popup-header">
                    <h3><i className="fas fa-handshake"></i> {tr('nouvelle_opportunite')}</h3>
                    <button className="crm-opp-popup-close" onClick={() => setCrmView('kanban')}><i className="fas fa-times"></i></button>
                  </div>
                  <div className="crm-opp-popup-body">
                    <div className="crm-opp-field">
                      <label className="crm-opp-label">{tr('intitule')} <span className="crm-opp-required">*</span></label>
                      <input className="crm-opp-input" type="text" value={newOpp.intitule} onChange={e => setNewOpp(p => ({ ...p, intitule: e.target.value }))} autoFocus />
                    </div>
                    <div className="crm-opp-field">
                      <label className="crm-opp-label">{tr('destinataire')}</label>
                      <select className="crm-opp-select" value={newOpp.destinataire} onChange={e => setNewOpp(p => ({ ...p, destinataire: e.target.value }))}>
                        <option value="">{tr('choisir_client')}</option>
                        {clients.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="crm-opp-field">
                      <label className="crm-opp-label">{tr('montant_ht')} <span className="crm-opp-required">*</span></label>
                      <input className="crm-opp-input" type="number" placeholder="0.00" min="0" step="0.01" value={newOpp.montantHT} onChange={e => setNewOpp(p => ({ ...p, montantHT: e.target.value }))} />
                    </div>
                    <div className="crm-opp-field">
                      <label className="crm-opp-label">{tr('devise')}</label>
                      <select className="crm-opp-select" value={newOpp.devise} onChange={e => setNewOpp(p => ({ ...p, devise: e.target.value }))}>
                        <option value="EUR">Euro (€)</option><option value="USD">Dollar ($)</option>
                        <option value="XOF">Franc CFA (FCFA)</option><option value="XAF">Franc CFA CEMAC (FCFA)</option>
                        <option value="GBP">Livre sterling (£)</option><option value="MAD">Dirham (MAD)</option>
                        <option value="NGN">Naira (₦)</option>
                      </select>
                    </div>
                    <div className="crm-opp-field">
                      <label className="crm-opp-label">{tr('mois_signature')}</label>
                      <div className="crm-opp-row">
                        <select className="crm-opp-select" value={newOpp.moisSignature} onChange={e => setNewOpp(p => ({ ...p, moisSignature: e.target.value }))}>
                          <option value="">Mois</option>
                          {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].map((m, i) => (
                            <option key={i} value={i + 1}>{m}</option>
                          ))}
                        </select>
                        <select className="crm-opp-select" value={newOpp.anneeSignature} onChange={e => setNewOpp(p => ({ ...p, anneeSignature: e.target.value }))}>
                          <option value="">Année</option>
                          {[2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="crm-opp-field">
                      <label className="crm-opp-label">{tr('statut')} <span className="crm-opp-required">*</span></label>
                      <select className="crm-opp-select" value={newOpp.statut} onChange={e => setNewOpp(p => ({ ...p, statut: e.target.value }))}>
                        <option>Prospect</option><option>Contacté</option><option>Proposition</option><option>Négociation</option><option>Clôture</option>
                      </select>
                    </div>
                  </div>
                  <div className="crm-opp-popup-footer">
                    <button className="crm-opp-cancel-btn" onClick={() => setCrmView('kanban')}>{tr('annuler')}</button>
                    <button className="crm-opp-submit-btn" onClick={handleCreateOpportunity} disabled={!newOpp.intitule.trim()}>
                      <i className="fas fa-plus"></i> {tr('creer_opportunite')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Popup Nouvelle activité */}
            {showActivityModal && (
              <div className="crm-opp-overlay" onClick={() => setShowActivityModal(false)}>
                <div className="crm-opp-popup" onClick={e => e.stopPropagation()}>
                  <div className="crm-opp-popup-header">
                    <h3><i className="fas fa-tasks"></i> Nouvelle activité</h3>
                    <button className="crm-opp-popup-close" onClick={() => setShowActivityModal(false)}><i className="fas fa-times"></i></button>
                  </div>
                  <div className="crm-opp-popup-body">
                    <div className="crm-opp-field">
                      <label className="crm-opp-label">Titre <span className="crm-opp-required">*</span></label>
                      <input className="crm-opp-input" type="text" placeholder="Nom de l'activité" value={newActivity.titre} onChange={e => setNewActivity(p => ({ ...p, titre: e.target.value }))} autoFocus />
                    </div>
                    <div className="crm-opp-field">
                      <label className="crm-opp-label">{tr('date_echeance')}</label>
                      <div className="crm-opp-row">
                        <input className="crm-opp-input" type="date" value={newActivity.date} onChange={e => setNewActivity(p => ({ ...p, date: e.target.value }))} />
                        <input className="crm-opp-input" type="time" value={newActivity.heure} onChange={e => setNewActivity(p => ({ ...p, heure: e.target.value }))} />
                      </div>
                    </div>
                    <div className="crm-opp-field">
                      <label className="crm-opp-label">{tr('notes')}</label>
                      <textarea className="crm-opp-input" rows={3} style={{ resize: 'vertical' }} value={newActivity.notes} onChange={e => setNewActivity(p => ({ ...p, notes: e.target.value }))} />
                    </div>
                    <div className="crm-opp-field">
                      <label className="crm-act-checkbox-label">
                        <input type="checkbox" checked={newActivity.realise} onChange={e => setNewActivity(p => ({ ...p, realise: e.target.checked }))} />
                        <span>Marquer comme étant réalisé</span>
                      </label>
                    </div>
                    <div className="crm-opp-field">
                      <select className="crm-opp-select" value={newActivity.responsable} onChange={e => setNewActivity(p => ({ ...p, responsable: e.target.value }))}>
                        <option value="">— Responsable —</option>
                        {clients.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="crm-opp-popup-footer">
                    <button className="crm-opp-cancel-btn" onClick={() => setShowActivityModal(false)}>{tr('annuler')}</button>
                    <button className="crm-opp-submit-btn" onClick={handleCreateActivity} disabled={!newActivity.titre.trim()}>{tr('enregistrer')}</button>
                  </div>
                </div>
              </div>
            )}

            {/* Popup Export */}
            {showExportModal && (
              <div className="crm-opp-overlay" onClick={() => setShowExportModal(false)}>
                <div className="crm-act-export-popup" onClick={e => e.stopPropagation()}>
                  <div className="crm-act-export-header">
                    <h3>Exporter ({activities.length} activité{activities.length > 1 ? 's' : ''})</h3>
                    <button className="crm-opp-popup-close" onClick={() => setShowExportModal(false)}><i className="fas fa-times"></i></button>
                  </div>
                  <div className="crm-act-export-btns">
                    <button className="crm-act-export-btn-fmt pdf" onClick={() => exportActivities('pdf')}>PDF</button>
                    <button className="crm-act-export-btn-fmt csv" onClick={() => exportActivities('csv')}>CSV</button>
                    <button className="crm-act-export-btn-fmt excel" onClick={() => exportActivities('csv')}>Excel</button>
                    <button className="crm-act-export-btn-fmt json" onClick={() => exportActivities('json')}>JSON</button>
                  </div>
                  <p className="crm-act-export-note"><i className="fas fa-info-circle"></i> La génération peut prendre quelques secondes.</p>
                </div>
              </div>
            )}
          </div>
        )}

      </main>

      {showPreview && (
        <InvoicePreview
          formData={formData}
          subtotal={calculateSubtotal()}
          tax={calculateTax()}
          total={calculateTotal()}
          color={invoiceColor}
          paymentMethods={invoicePaymentMethods}
          showSig={showInvoiceSig}
          onClose={() => setShowPreview(false)}
          onDownload={generatePDF}
          sigFacture={sigFacture}
          setSigFacture={setSigFacture}
        />
      )}

      {showDevisPreview && (
        <DevisPreview
          devisData={devisData}
          subtotal={calcDevisSubtotal()}
          tax={calcDevisTax()}
          total={calcDevisTotal()}
          color={devisColor}
          paymentMethods={devisPaymentMethods}
          showSig={showDevisSig}
          devisOptions={devisOptions}
          onClose={() => setShowDevisPreview(false)}
          onDownload={generateDevisPDF}
          onConvertToInvoice={() => { handleConvertToInvoice(); setShowDevisPreview(false); }}
          sigPrestataire={sigPrestataire}
          setSigPrestataire={setSigPrestataire}
        />
      )}

      {showClientModal && (
        <ClientModal
          client={editingClient}
          onClose={() => { setShowClientModal(false); setEditingClient(null); }}
          onSave={handleSaveClient}
        />
      )}
    </div>
  );
};

export default Invoice;